import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { DataGrid, GridColDef, GridFooterContainer, GridPagination, GridRowSelectionModel } from '@mui/x-data-grid';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AgGridReact } from 'ag-grid-react';
import { useDispatch } from 'react-redux';
import * as XLSX from 'xlsx';
import apiService from '../services/api.service';
import { setLoading, showSnackbar } from '../store/slices/uiSlice';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

type ProjectOption = { code: string; name?: string };
type DimensionOption = { code: string; name?: string };
type AccountOption = { code: string; name?: string };
type AdvancedGLRule = {
  absoluteEntry: number;
  code?: string;
  description?: string;
  costAccount: string;
  fromDate?: string;
  toDate?: string;
  udf1?: string;
  udf2?: string;
};
type LineDefaultKey = 'projectCode' | 'dim1' | 'dim2' | 'dim3' | 'dim4' | 'dim5';
type GridChooseField = LineDefaultKey | 'cogsAccountCode' | 'debitAccountCode' | 'creditAccountCode';
type GridChooseOption = { code: string; name?: string | null; label: string };

const dimensionLabels: Record<Exclude<LineDefaultKey, 'projectCode'>, string> = {
  dim1: 'Division',
  dim2: 'Department',
  dim3: 'Project Cost Code',
  dim4: 'Location',
  dim5: 'Auxiliary',
};
type CustomerOption = {
  CardCode: string;
  CardName?: string;
  Currency?: string;
  U_Auto_Invoice_Margin?: string | number | null;
  Data?: Record<string, any>;
};
type InvoiceType = 'charge-back' | 'special-project';
type InvoiceLineMode = 'single' | 'multiple';
type ProcessMode = 'direct' | 'final-unbilled';
type DraftGrouping = 'combined' | 'separate';
type LoadedItemFilter = 'GRPO' | 'Sales Order' | 'Manual JE' | 'All';
type UserChosenFieldMap = Partial<Record<GridChooseField, boolean>>;
type GRPORow = {
  id: number | string;
  data?: Record<string, any>;
  transactionType?: 'GRPO' | 'Sales Order' | 'Manual JE';
  grpoDocEntry: number | null;
  salesOrderDocEntry?: number | null;
  journalEntryDocNum?: number | null;
  manualJEEntry?: boolean;
  manualAmountInput?: string;
  sourceDim5?: string;
  dim5Overridden?: boolean;
  debitAccountCode?: string | null;
  creditAccountCode?: string | null;
  grpoDocNum?: number | null;
  documentNo?: number | string | null;
  docDate?: string | null;
  projectCode: string;
  projectCodes?: string[];
  cardCode?: string | null;
  cardName?: string | null;
  currency?: string | null;
  localCurrency?: string | null;
  sourceCurrency?: string | null;
  sourceAmount?: number;
  exchangeRate?: number | null;
  dim1?: string | null;
  dim2?: string | null;
  dim3?: string | null;
  dim4?: string | null;
  dim5?: string | null;
  hasMultipleDimensions?: boolean;
  invoiceStatus: 'Accrued' | 'Drafted' | 'Invoiced' | 'Pending' | 'Reference' | 'Ready' | 'Partial';
  isProcessable?: boolean;
  grpoCost: number;
  salesPrice: number;
  salesOrderOriginalAmount?: number;
  salesOrderBilledBaseAmount?: number;
  salesOrderBilledInvoiceAmount?: number;
  salesOrderRemainingAmount?: number;
  salesOrderBillingCount?: number;
  salesOrderLastBillingDate?: string | null;
  journalEntryBillingCount?: number;
  journalEntryOriginalAmount?: number;
  journalEntryDebitAmount?: number;
  journalEntryCreditAmount?: number;
  journalEntryNetAmount?: number;
  journalEntryProjectLineCount?: number;
  journalEntryBilledBaseAmount?: number;
  journalEntryBilledInvoiceAmount?: number;
  journalEntryRemainingAmount?: number;
  arInvoiceDocNum?: number | string | null;
  goodsIssueDocNum?: number | string | null;
  cogsAccountCode?: string | null;
  cogsAccountCodes?: string[];
  cogsMappingSource?: string | null;
  itemCodes?: string[];
  cogsJournalEntryDocEntry?: number | null;
  cogsJournalEntryDocNum?: number | null;
  isUnbilledRevenue?: boolean;
  unbilledRevenueAmount?: number;
  unbilledOriginalAmount?: number;
  unbilledFinalizedAmount?: number;
  unbilledRemainingAmount?: number;
  unbilledAccountCode?: string | null;
  companyUnbilledAccountCode?: string | null;
  unbilledFinalStatus?: string | null;
  unbilledJournalEntryDocEntry?: number | null;
  unbilledJournalEntryDocNum?: number | null;
  reversalJournalEntryDocEntry?: number | null;
  reversalJournalEntryDocNum?: number | null;
  userChosenFields?: UserChosenFieldMap;
};

type SalesOrderBillingHistory = {
  salesOrder?: Record<string, any>;
  manualJournal?: Record<string, any>;
  summary?: {
    orderBaseAmount?: number;
    journalBaseAmount?: number;
    billedBaseAmount?: number;
    billedInvoiceAmount?: number;
    remainingBaseAmount?: number;
    billingCount?: number;
    isFullyBilled?: boolean;
  };
  rows?: any[];
};

type HistoryRow = {
  id: number;
  runid: number;
  companyid: number;
  transactiontype?: string | null;
  projectcode: string;
  grpodocentry: number;
  grpodocnum?: number | null;
  grpodocdate?: string | null;
  salesorderdocentry?: number | null;
  salesorderdocnum?: number | null;
  sourcedocentry?: number | null;
  sourcedocnum?: number | null;
  sourcedocdate?: string | null;
  grpocost: string | number;
  salesprice: string | number;
  goodsissuedocentry?: number | null;
  goodsissuedocnum?: number | null;
  arinvoicedocentry?: number | null;
  arinvoicedocnum?: number | null;
  cogsaccountcode?: string | null;
  cogsjournalentrydocentry?: number | null;
  cogsjournalentrydocnum?: number | null;
  status: string;
  customercode?: string | null;
  invoicetype?: string | null;
  invoicelinemode?: string | null;
  processmode?: string | null;
  draftgrouping?: string | null;
  itemcode?: string | null;
  itemname?: string | null;
  marginpercent?: string | number | null;
  runcreateddate?: string | null;
  runfinisheddate?: string | null;
};

type HistoryFilters = {
  projectCode: string;
  customerCode: string;
  arDocNum: string;
  goodsIssueDocNum: string;
  fromDate: string;
  toDate: string;
  q: string;
};

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
};

// The backend stamps SAP documents in Asia/Riyadh, so the posting date must
// default to the same calendar day. toISOString() would show the previous day
// until 03:00 local time.
const riyadhToday = () => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Riyadh', year: 'numeric', month: '2-digit', day: '2-digit',
}).format(new Date());

const parseIsoDate = (value: string): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
};

const toIsoDate = (value: Date | null): string => {
  if (!value || Number.isNaN(value.getTime())) return '';
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDisplayDate = (value: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
  return match ? `${match[3]}/${match[2]}/${match[1]}` : value || '-';
};

const money = (value: unknown, currency?: string | null) => {
  const num = Number(value || 0);
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
  return currency ? `${formatted} ${currency}` : formatted;
};

const parseMaybeJsonMessage = (value: any): string => {
  if (!value) return '';
  if (typeof value !== 'string') return String(value);
  try {
    const parsed = JSON.parse(value);
    return parsed?.error?.message || parsed?.message || value;
  } catch {
    return value;
  }
};

const getErrorMessage = (error: any, fallback: string): string =>
  parseMaybeJsonMessage(error?.response?.data?.message) ||
  parseMaybeJsonMessage(error?.response?.data?.error?.message) ||
  parseMaybeJsonMessage(error?.response?.data?.error) ||
  error?.message ||
  fallback;

const invoiceItems: Record<InvoiceType, { label: string; itemCode: string; itemName: string }> = {
  'charge-back': { label: 'Charge Back', itemCode: 'SA00005', itemName: 'CHARGE BACK' },
  'special-project': { label: 'Special Project', itemCode: 'SA00004', itemName: 'SPECIAL PROJECT' },
};

const calculateWithMargin = (baseAmount: unknown, marginPercent: unknown): number => {
  const parsedBase = Number(baseAmount || 0);
  const base = Number.isFinite(parsedBase) ? parsedBase : 0;
  const margin = Number(marginPercent || 0);
  const safeMargin = Number.isFinite(margin) ? margin : 0;
  return Number((base + (base * safeMargin / 100)).toFixed(2));
};

const AutoInvoice: React.FC = () => {
  const dispatch = useDispatch();
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [dimensions, setDimensions] = useState<Record<'dim1' | 'dim2' | 'dim3' | 'dim4' | 'dim5', DimensionOption[]>>({
    dim1: [],
    dim2: [],
    dim3: [],
    dim4: [],
    dim5: [],
  });
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [advancedGLRules, setAdvancedGLRules] = useState<AdvancedGLRule[]>([]);
  const [invoiceType, setInvoiceType] = useState<InvoiceType>('charge-back');
  const [invoiceLineMode, setInvoiceLineMode] = useState<InvoiceLineMode>('single');
  const [processMode, setProcessMode] = useState<ProcessMode>('direct');
  const [draftGrouping, setDraftGrouping] = useState<DraftGrouping>('combined');
  const [selectedProjectCodes, setSelectedProjectCodes] = useState<string[]>([]);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [projectDialogSearch, setProjectDialogSearch] = useState('');
  const [draftProjectCodes, setDraftProjectCodes] = useState<string[]>([]);
  const [gridChooseDialog, setGridChooseDialog] = useState<{
    open: boolean;
    rowId: number | string | null;
    field: GridChooseField;
    title: string;
    search: string;
    draftCode: string;
  }>({
    open: false,
    rowId: null,
    field: 'projectCode',
    title: '',
    search: '',
    draftCode: '',
  });
  const [arHeaderProjectCode, setArHeaderProjectCode] = useState('');
  const [draftArHeaderProjectCode, setDraftArHeaderProjectCode] = useState('');
  const [arHeaderProjectDialogOpen, setArHeaderProjectDialogOpen] = useState(false);
  const [arHeaderProjectSearch, setArHeaderProjectSearch] = useState('');
  const [customerCode, setCustomerCode] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [draftCustomerCode, setDraftCustomerCode] = useState('');
  const [fromDate, setFromDate] = useState(monthStart());
  const [toDate, setToDate] = useState(today());
  const [postingDate, setPostingDate] = useState(riyadhToday());
  const [marginPercent, setMarginPercent] = useState(0);
  const [marginOverridden, setMarginOverridden] = useState(false);
  const [kpiDeduction, setKpiDeduction] = useState(0);
  const [headerAuxiliaryCode, setHeaderAuxiliaryCode] = useState('');
  const [rows, setRows] = useState<GRPORow[]>([]);
  const [gridSearch, setGridSearch] = useState('');
  const [loadedItemFilter, setLoadedItemFilter] = useState<LoadedItemFilter>('All');
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processResult, setProcessResult] = useState<{
    open: boolean;
    severity: 'success' | 'error';
    title: string;
    message: string;
    details?: string;
    summary?: Array<{ label: string; value: string }>;
    canViewUnbilledJEs?: boolean;
  }>({ open: false, severity: 'success', title: '', message: '' });
  const [processPreview, setProcessPreview] = useState<{
    open: boolean;
    payload: any | null;
    rows: GRPORow[];
  }>({ open: false, payload: null, rows: [] });
  const [activeTab, setActiveTab] = useState(0);
  const [historyRows, setHistoryRows] = useState<HistoryRow[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [salesOrderHistoryOpen, setSalesOrderHistoryOpen] = useState(false);
  const [salesOrderHistoryLoading, setSalesOrderHistoryLoading] = useState(false);
  const [salesOrderHistory, setSalesOrderHistory] = useState<SalesOrderBillingHistory | null>(null);
  const [manualJEHistoryOpen, setManualJEHistoryOpen] = useState(false);
  const [manualJEHistoryLoading, setManualJEHistoryLoading] = useState(false);
  const [manualJEHistory, setManualJEHistory] = useState<SalesOrderBillingHistory | null>(null);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyPageSize, setHistoryPageSize] = useState(25);
  const [historyFilters, setHistoryFilters] = useState<HistoryFilters>({
    projectCode: '',
    customerCode: '',
    arDocNum: '',
    goodsIssueDocNum: '',
    fromDate: monthStart(),
    toDate: today(),
    q: '',
  });
  const [processColumnWidths, setProcessColumnWidths] = useState<Record<string, number>>({});
  const [historyColumnWidths, setHistoryColumnWidths] = useState<Record<string, number>>({});

  const companyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined;
  const projectGridRef = useRef<AgGridReact<ProjectOption>>(null);
  const arHeaderProjectGridRef = useRef<AgGridReact<ProjectOption>>(null);
  const customerGridRef = useRef<AgGridReact<CustomerOption>>(null);
  const projectCode = selectedProjectCodes[0] || '';
  const selectedProjectLabel = selectedProjectCodes.length === 0
    ? ''
    : selectedProjectCodes.length === 1
      ? selectedProjectCodes[0]
      : `${selectedProjectCodes.length} projects selected`;
  const arHeaderProjectLabel = arHeaderProjectCode || '';
  const selectedCustomer = useMemo(
    () => customers.find((customer) => customer.CardCode === customerCode) || null,
    [customers, customerCode],
  );
  const selectedCustomerLabel = selectedCustomer
    ? `${selectedCustomer.CardCode}${selectedCustomer.CardName ? ` - ${selectedCustomer.CardName}` : ''}`
    : '';

  const loadLookups = useCallback(async () => {
    if (!companyId) return;
    setAdvancedGLRules([]);
    try {
      const res = await apiService.getAutoInvoiceLookups(companyId);
      const payload = res?.data || {};
      const projectData = Array.isArray(payload.projects) ? payload.projects : [];
      setProjects(projectData);
      setDimensions({
        dim1: Array.isArray(payload.dimensions?.dim1) ? payload.dimensions.dim1 : [],
        dim2: Array.isArray(payload.dimensions?.dim2) ? payload.dimensions.dim2 : [],
        dim3: Array.isArray(payload.dimensions?.dim3) ? payload.dimensions.dim3 : [],
        dim4: Array.isArray(payload.dimensions?.dim4) ? payload.dimensions.dim4 : [],
        dim5: Array.isArray(payload.dimensions?.dim5) ? payload.dimensions.dim5 : [],
      });
      setAccounts(Array.isArray(payload.accounts) ? payload.accounts : []);
      setAdvancedGLRules(Array.isArray(payload.advancedGLRules) ? payload.advancedGLRules : []);
    } catch (error: any) {
      dispatch(showSnackbar({ message: getErrorMessage(error, 'Failed to load auto invoice lookups'), severity: 'error' }));
    }
  }, [companyId, dispatch]);

  const loadCustomers = useCallback(async (q = '') => {
    if (!companyId) return;
    try {
      const res = await apiService.getAutoInvoiceCustomers(companyId, q);
      const data = Array.isArray(res?.data) ? res.data : [];
      setCustomers(data);
    } catch (error: any) {
      dispatch(showSnackbar({ message: getErrorMessage(error, 'Failed to load customers'), severity: 'error' }));
    }
  }, [companyId, dispatch]);

  useEffect(() => {
    void loadLookups();
    void loadCustomers();
  }, [loadLookups, loadCustomers]);

  const filteredProjectOptions = useMemo(() => {
    const term = projectDialogSearch.trim().toLowerCase();
    if (!term) return projects;
    return projects.filter((project) => (
      String(project.code || '').toLowerCase().includes(term)
      || String(project.name || '').toLowerCase().includes(term)
    ));
  }, [projectDialogSearch, projects]);

  const filteredArHeaderProjectOptions = useMemo(() => {
    const availableProjects = selectedProjectCodes.length
      ? projects.filter((project) => selectedProjectCodes.includes(project.code))
      : projects;
    const term = arHeaderProjectSearch.trim().toLowerCase();
    if (!term) return availableProjects;
    return availableProjects.filter((project) => (
      String(project.code || '').toLowerCase().includes(term)
      || String(project.name || '').toLowerCase().includes(term)
    ));
  }, [arHeaderProjectSearch, projects, selectedProjectCodes]);

  const filteredCustomerOptions = useMemo(() => {
    const term = customerSearch.trim().toLowerCase();
    if (!term) return customers;
    return customers.filter((customer) => (
      String(customer.CardCode || '').toLowerCase().includes(term)
      || String(customer.CardName || '').toLowerCase().includes(term)
      || String(customer.Currency || '').toLowerCase().includes(term)
    ));
  }, [customerSearch, customers]);

  const getLineDefaultOptionLabel = useCallback((key: LineDefaultKey, code: unknown): string => {
    const normalized = String(code || '').trim();
    if (!normalized) return '';
    const source = key === 'projectCode' ? projects : dimensions[key];
    const match = (source || []).find((option) => String(option.code || '').toUpperCase() === normalized.toUpperCase());
    return match?.name ? `${normalized} - ${match.name}` : normalized;
  }, [dimensions, projects]);

  const openProjectDialog = () => {
    setDraftProjectCodes(selectedProjectCodes);
    setProjectDialogSearch('');
    setProjectDialogOpen(true);
  };

  const syncProjectGridSelection = useCallback(() => {
    const api = projectGridRef.current?.api;
    if (!api) return;
    const selected = new Set(draftProjectCodes);
    api.forEachNode((node) => {
      node.setSelected(selected.has(String(node.data?.code || '')));
    });
  }, [draftProjectCodes]);

  useEffect(() => {
    if (projectDialogOpen) {
      window.setTimeout(syncProjectGridSelection, 0);
    }
  }, [filteredProjectOptions, projectDialogOpen, syncProjectGridSelection]);

  const applyProjectSelection = () => {
    const api = projectGridRef.current?.api;
    const selectedCodes = api
      ? api.getSelectedRows().map((row) => String(row.code || '').trim()).filter(Boolean)
      : draftProjectCodes;
    const nextProjectCodes = Array.from(new Set(selectedCodes));
    setSelectedProjectCodes(nextProjectCodes);
    if (nextProjectCodes.length === 1) {
      setArHeaderProjectCode(nextProjectCodes[0]);
    } else if (!nextProjectCodes.includes(arHeaderProjectCode)) {
      setArHeaderProjectCode('');
    }
    setRows([]);
    setSelection([]);
    setProjectDialogOpen(false);
    if (nextProjectCodes.length > 1) {
      setDraftArHeaderProjectCode(nextProjectCodes.includes(arHeaderProjectCode) ? arHeaderProjectCode : '');
      setArHeaderProjectSearch('');
      setArHeaderProjectDialogOpen(true);
    }
  };

  const syncArHeaderProjectGridSelection = useCallback(() => {
    const api = arHeaderProjectGridRef.current?.api;
    if (!api) return;
    api.forEachNode((node) => {
      node.setSelected(String(node.data?.code || '') === draftArHeaderProjectCode);
    });
  }, [draftArHeaderProjectCode]);

  useEffect(() => {
    if (arHeaderProjectDialogOpen) {
      window.setTimeout(syncArHeaderProjectGridSelection, 0);
    }
  }, [arHeaderProjectDialogOpen, filteredArHeaderProjectOptions, syncArHeaderProjectGridSelection]);

  const applyArHeaderProject = () => {
    const selected = arHeaderProjectGridRef.current?.api?.getSelectedRows()?.[0];
    const selectedCode = String(selected?.code || draftArHeaderProjectCode || '').trim();
    if (!selectedCode) {
      dispatch(showSnackbar({ message: 'Select AR header project', severity: 'warning' }));
      return;
    }
    setArHeaderProjectCode(selectedCode);
    setArHeaderProjectDialogOpen(false);
  };

  const syncCustomerGridSelection = useCallback(() => {
    const api = customerGridRef.current?.api;
    if (!api) return;
    api.forEachNode((node) => {
      node.setSelected(String(node.data?.CardCode || '') === draftCustomerCode);
    });
  }, [draftCustomerCode]);

  useEffect(() => {
    if (customerDialogOpen) {
      window.setTimeout(syncCustomerGridSelection, 0);
    }
  }, [customerDialogOpen, filteredCustomerOptions, syncCustomerGridSelection]);

  const openCustomerDialog = () => {
    setDraftCustomerCode(customerCode);
    setCustomerSearch('');
    setCustomerDialogOpen(true);
  };

  const applyCustomerSelection = () => {
    const selected = customerGridRef.current?.api?.getSelectedRows()?.[0];
    const selectedCode = String(selected?.CardCode || draftCustomerCode || '').trim();
    if (!selectedCode) {
      dispatch(showSnackbar({ message: 'Select customer', severity: 'warning' }));
      return;
    }
    setCustomerCode(selectedCode);
    setMarginPercent(0);
    setMarginOverridden(false);
    setRows([]);
    setSelection([]);
    setCustomerDialogOpen(false);
  };

  const loadHistory = useCallback(async () => {
    if (!companyId) return;
    setHistoryLoading(true);
    try {
      const res = await apiService.getAutoInvoiceHistory({
        companyId,
        ...historyFilters,
        page: historyPage + 1,
        limit: historyPageSize,
      });
      const payload = res?.data || {};
      const data = Array.isArray(payload.rows) ? payload.rows : [];
      setHistoryRows(data.map((row: HistoryRow) => ({
        ...row,
        id: Number(row.id || `${row.runid}${row.transactiontype || 'GRPO'}${row.sourcedocentry || row.grpodocentry || row.salesorderdocentry || 0}`),
      })));
      setHistoryTotal(Number(payload.total || 0));
    } catch (error: any) {
      dispatch(showSnackbar({ message: getErrorMessage(error, 'Failed to load auto invoice history'), severity: 'error' }));
    } finally {
      setHistoryLoading(false);
    }
  }, [companyId, dispatch, historyFilters, historyPage, historyPageSize]);

  useEffect(() => {
    if (activeTab === 1) void loadHistory();
  }, [activeTab, loadHistory]);

  const openSalesOrderHistory = async (row: GRPORow) => {
    if (!companyId || !row.salesOrderDocEntry) return;
    setSalesOrderHistoryOpen(true);
    setSalesOrderHistoryLoading(true);
    setSalesOrderHistory(null);
    try {
      const res = await apiService.getAutoInvoiceSalesOrderHistory(companyId, Number(row.salesOrderDocEntry));
      setSalesOrderHistory(res?.data || null);
    } catch (error: any) {
      dispatch(showSnackbar({ message: getErrorMessage(error, 'Failed to load sales order billing history'), severity: 'error' }));
    } finally {
      setSalesOrderHistoryLoading(false);
    }
  };

  const openManualJEHistory = async (row: GRPORow) => {
    if (!companyId || !row.journalEntryDocNum) return;
    setManualJEHistoryOpen(true);
    setManualJEHistoryLoading(true);
    setManualJEHistory(null);
    try {
      const res = await apiService.getAutoInvoiceManualJournalHistory(companyId, Number(row.journalEntryDocNum));
      setManualJEHistory(res?.data || null);
    } catch (error: any) {
      dispatch(showSnackbar({ message: getErrorMessage(error, 'Failed to load manual JE billing history'), severity: 'error' }));
    } finally {
      setManualJEHistoryLoading(false);
    }
  };

  const loadGRPOsForMode = async (mode: ProcessMode) => {
    if (!companyId || selectedProjectCodes.length === 0 || !fromDate || !toDate) {
      dispatch(showSnackbar({ message: 'Select one or more projects and period first', severity: 'warning' }));
      return;
    }
    if (mode === 'final-unbilled' && !customerCode.trim()) {
      dispatch(showSnackbar({ message: 'Select the customer linked to the unbilled journal entries', severity: 'warning' }));
      return;
    }
    setLoadingRows(true);
    try {
      const res = await apiService.searchAutoInvoiceGRPOs({
        companyId,
        projectCode,
        projectCodes: selectedProjectCodes,
        fromDate,
        toDate,
        marginPercent,
        customerCode: customerCode.trim(),
        processMode: mode,
      });
      const data: GRPORow[] = Array.isArray(res?.data) ? res.data : [];
      setRows(data.map((row) => {
        const sourceDim5 = String(row.sourceDim5 ?? row.dim5 ?? '').trim();
        const headerAuxiliary = mode === 'direct' ? headerAuxiliaryCode : '';
        const auxiliary = headerAuxiliary || sourceDim5;
        const userChosenFields = { ...(row.userChosenFields || {}) };
        if (headerAuxiliary) userChosenFields.dim5 = true;
        return {
          ...row,
          sourceDim5,
          dim5: auxiliary,
          manualAmountInput: row.manualJEEntry ? (row.grpoCost ? String(row.grpoCost) : '') : row.manualAmountInput,
          userChosenFields,
        };
      }));
      setSelection([]);
    } catch (error: any) {
      dispatch(showSnackbar({ message: getErrorMessage(error, 'Failed to load GRPOs'), severity: 'error' }));
      setRows([]);
    } finally {
      setLoadingRows(false);
    }
  };

  const loadGRPOs = async () => loadGRPOsForMode(processMode);

  const selectedRows = useMemo(
    () => rows.filter((row) => (
      selection.includes(row.id)
      && (
        row.isProcessable === true
        || ((row.transactionType === 'GRPO' || !row.transactionType) && row.invoiceStatus === 'Pending' && row.grpoDocEntry)
        || (row.transactionType === 'Sales Order' && row.salesOrderDocEntry && Number(row.salesOrderRemainingAmount ?? row.grpoCost) > 0)
        || (row.transactionType === 'Manual JE' && (row.manualJEEntry || row.journalEntryDocNum) && !!row.isProcessable)
      )
    )),
    [rows, selection],
  );

  const filteredRows = useMemo(() => {
    const typeRows = processMode === 'direct' && loadedItemFilter !== 'All'
      ? rows.filter((row) => (row.transactionType || 'GRPO') === loadedItemFilter)
      : rows;
    const term = gridSearch.trim().toLowerCase();
    if (!term) return typeRows;
    return typeRows.filter((row) => [
      row.grpoDocEntry,
      row.grpoDocNum,
      row.docDate,
      row.projectCode,
      row.dim1,
      row.dim2,
      row.dim3,
      row.dim4,
      row.dim5,
      row.cardCode,
      row.cardName,
      row.invoiceStatus,
      row.arInvoiceDocNum,
      row.goodsIssueDocNum,
      row.transactionType,
      row.documentNo,
    ].some((value) => String(value ?? '').toLowerCase().includes(term)));
  }, [gridSearch, loadedItemFilter, processMode, rows]);

  const loadedItemCounts = useMemo(() => ({
    GRPO: rows.filter((row) => (row.transactionType || 'GRPO') === 'GRPO').length,
    'Sales Order': rows.filter((row) => row.transactionType === 'Sales Order').length,
    'Manual JE': rows.filter((row) => row.transactionType === 'Manual JE').length,
    All: rows.length,
  }), [rows]);

  const totals = useMemo(() => selectedRows.reduce((acc, row) => ({
    cost: acc.cost + Number(row.grpoCost || 0),
    sales: acc.sales + Number(row.salesPrice || 0),
  }), { cost: 0, sales: 0 }), [selectedRows]);

  const adjustedTotals = useMemo(() => {
    const appliedKpi = processMode === 'final-unbilled'
      ? 0
      : Math.min(Math.max(0, Number(kpiDeduction || 0)), Math.max(totals.sales, 0));
    const netAfterKpi = totals.sales - appliedKpi;
    return {
      appliedKpi,
      netAfterKpi,
      arValue: Number(netAfterKpi.toFixed(2)),
    };
  }, [kpiDeduction, processMode, totals.sales]);

  const rowKpiAllocation = useMemo(() => {
    const allocation = new Map<number | string, { kpi: number; final: number }>();
    const totalBeforeKpi = selectedRows.reduce((sum, row) => sum + Number(row.salesPrice || 0), 0);
    const positiveRows = selectedRows.filter((row) => Number(row.salesPrice || 0) > 0);
    const positiveTotal = positiveRows.reduce((sum, row) => sum + Number(row.salesPrice || 0), 0);
    const appliedKpi = processMode === 'final-unbilled'
      ? 0
      : Math.min(Math.max(0, Number(kpiDeduction || 0)), Math.max(totalBeforeKpi, 0));
    let allocated = 0;

    selectedRows.forEach((row) => {
      const beforeKpi = Number(row.salesPrice || 0);
      const positiveIndex = positiveRows.indexOf(row);
      const kpiShare = positiveIndex >= 0 && positiveTotal > 0
        ? (positiveIndex === positiveRows.length - 1
          ? Number((appliedKpi - allocated).toFixed(2))
          : Number((appliedKpi * (beforeKpi / positiveTotal)).toFixed(2)))
        : 0;
      allocated = Number((allocated + kpiShare).toFixed(2));
      allocation.set(row.id, {
        kpi: kpiShare,
        final: Number((beforeKpi - kpiShare).toFixed(2)),
      });
    });

    return allocation;
  }, [kpiDeduction, processMode, selectedRows]);

  const previewAccountingBreakdown = useMemo(() => {
    const labels = ['GRPO', 'Sales Order', 'Manual JE'];
    return labels.map((label) => {
      const typeRows = processPreview.rows.filter((row) => (row.transactionType || 'GRPO') === label);
      const baseAmount = typeRows.reduce((sum, row) => sum + Number(row.grpoCost || 0), 0);
      const unbilledAmount = typeRows.reduce(
        (sum, row) => sum + (rowKpiAllocation.get(row.id)?.final ?? Number(row.salesPrice || 0)),
        0,
      );
      const cogsAccounts = Array.from(new Set(typeRows.flatMap((row) => (
        row.manualJEEntry
          ? (row.debitAccountCode ? [row.debitAccountCode] : [])
          : row.cogsAccountCode ? [row.cogsAccountCode] : (row.cogsAccountCodes || [])
      )).filter(Boolean)));
      const manualCreditAccounts = Array.from(new Set(typeRows
        .filter((row) => row.manualJEEntry)
        .map((row) => row.creditAccountCode)
        .filter(Boolean)));
      return {
        type: label,
        count: typeRows.length,
        baseAmount,
        unbilledAmount,
        cogsAmount: label === 'Sales Order'
          ? 0
          : label === 'Manual JE'
            ? typeRows.reduce((sum, row) => sum + Math.abs(Number(row.grpoCost || 0)), 0)
            : baseAmount,
        cogsAccounts,
        cogsPosting: label === 'GRPO' ? 'Goods Issue' : label === 'Manual JE' ? 'COGS reclassification JE' : 'No COGS posting',
        creditAccount: label === 'GRPO'
          ? 'Inventory G/L (SAP item/warehouse determination)'
          : label === 'Manual JE'
            ? (manualCreditAccounts.join(', ') || 'Original inventory G/L from source JE')
            : '-',
      };
    }).filter((item) => item.count > 0);
  }, [processPreview.rows, rowKpiAllocation]);

  const previewUnbilledTotal = useMemo(
    () => previewAccountingBreakdown.reduce((sum, item) => sum + item.unbilledAmount, 0),
    [previewAccountingBreakdown],
  );

  // Per-company unbilled revenue G/L, reported by the backend; null means the
  // company has no unbilled revenue account and processing will be refused.
  const companyUnbilledAccount = useMemo(
    () => rows.find((row) => row.companyUnbilledAccountCode)?.companyUnbilledAccountCode || null,
    [rows],
  );
  const unbilledAccountLabel = companyUnbilledAccount || 'not configured';

  const previewReversalRevenueAccounts = useMemo(() => Array.from(new Set(
    processPreview.rows.flatMap((row) => {
      const journalLines = row.data?.JournalEntryLines;
      const lines = Array.isArray(journalLines) ? journalLines : [];
      return lines
        .filter((line: any) => Number(line?.Credit || 0) > 0)
        .map((line: any) => String(line?.AccountCode || line?.ControlAccount || '').trim())
        .filter((account: string) => account && account !== companyUnbilledAccount);
    }),
  )), [processPreview.rows, companyUnbilledAccount]);

  useEffect(() => {
    if (processMode === 'final-unbilled') return;
    setRows((prev) => prev.map((row) => (
      row.transactionType === 'Sales Order'
        ? { ...row, salesPrice: calculateWithMargin(row.grpoCost, marginPercent) }
        : row.transactionType === 'Manual JE'
          ? { ...row, salesPrice: calculateWithMargin(row.grpoCost, marginPercent) }
          : (row.transactionType === 'GRPO' || !row.transactionType)
            ? { ...row, salesPrice: calculateWithMargin(row.grpoCost, marginPercent) }
        : row
    )));
  }, [marginPercent, processMode]);

  const buildProcessPayload = (rowsToProcess: GRPORow[]) => ({
    companyId,
    projectCode,
    projectCodes: selectedProjectCodes,
    arHeaderProjectCode: arHeaderProjectCode.trim(),
    invoiceType,
    invoiceLineMode,
    processMode,
    draftGrouping,
    customerCode: customerCode.trim(),
    fromDate,
    toDate,
    postingDate,
    marginPercent: processMode === 'final-unbilled' ? 0 : marginPercent,
    kpiDeduction: processMode === 'final-unbilled' ? 0 : kpiDeduction,
    selections: rowsToProcess.map((row) => ({
      transactionType: row.transactionType || 'GRPO',
      manualJEEntry: Boolean(row.manualJEEntry),
      grpoDocEntry: row.grpoDocEntry ? Number(row.grpoDocEntry) : undefined,
      salesOrderDocEntry: row.salesOrderDocEntry ? Number(row.salesOrderDocEntry) : undefined,
      journalEntryDocNum: row.journalEntryDocNum ? Number(row.journalEntryDocNum) : undefined,
      baseAmount: Number(row.grpoCost || 0),
      invoiceAmount: Number(row.salesPrice || 0),
      projectCode: row.projectCode,
      projectCodes: row.projectCodes?.length ? row.projectCodes : [row.projectCode],
      // Send only explicit overrides. Source/rule values are recalculated on
      // the server immediately before posting so stale search results cannot win.
      dim1: row.userChosenFields?.dim1 ? row.dim1 : undefined,
      dim2: row.userChosenFields?.dim2 ? row.dim2 : undefined,
      dim3: row.userChosenFields?.dim3 ? row.dim3 : undefined,
      dim4: row.userChosenFields?.dim4 ? row.dim4 : undefined,
      dim5: row.userChosenFields?.dim5 ? row.dim5 : undefined,
      cogsAccountCode: row.userChosenFields?.cogsAccountCode ? row.cogsAccountCode : undefined,
      debitAccountCode: row.userChosenFields?.debitAccountCode ? row.debitAccountCode : undefined,
      creditAccountCode: row.userChosenFields?.creditAccountCode ? row.creditAccountCode : undefined,
    })),
  });

  const processSelected = async () => {
    if (!companyId || !invoiceType || selectedProjectCodes.length === 0 || !customerCode.trim()) {
      dispatch(showSnackbar({ message: 'Type, at least one project and customer are required', severity: 'warning' }));
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(postingDate)) {
      dispatch(showSnackbar({ message: 'Enter a valid posting date', severity: 'warning' }));
      return;
    }
    if (!arHeaderProjectCode.trim()) {
      dispatch(showSnackbar({ message: 'Select AR header project before processing', severity: 'warning' }));
      setDraftArHeaderProjectCode(selectedProjectCodes.includes(arHeaderProjectCode) ? arHeaderProjectCode : '');
      setArHeaderProjectSearch('');
      setArHeaderProjectDialogOpen(true);
      return;
    }
    if (selectedRows.length === 0) {
      dispatch(showSnackbar({
        message: processMode === 'final-unbilled'
          ? 'Select at least one unbilled revenue Journal Entry'
          : 'Select at least one pending GRPO, Sales Order or Manual JE',
        severity: 'warning',
      }));
      return;
    }
    if (processMode === 'final-unbilled') {
      const invalidAmount = selectedRows.find((row) => (
        !(Number(row.salesPrice) > 0)
        || Number(row.salesPrice) - Number(row.unbilledRemainingAmount ?? row.grpoCost) > 0.01
      ));
      if (invalidAmount) {
        dispatch(showSnackbar({
          message: `Invoice amount for JE ${invalidAmount.journalEntryDocNum || invalidAmount.documentNo || ''} must be greater than zero and cannot exceed the remaining amount`,
          severity: 'warning',
        }));
        return;
      }
    }
    if (processMode === 'direct' && adjustedTotals.arValue <= 0) {
      dispatch(showSnackbar({
        message: 'Net AR must be greater than zero after adding debits and subtracting credits. Use a credit memo workflow for a zero or negative balance.',
        severity: 'warning',
      }));
      return;
    }
    if (processMode === 'direct' && invoiceLineMode === 'multiple'
      && selectedRows.some((row) => Number(row.salesPrice || 0) < 0)) {
      dispatch(showSnackbar({
        message: 'Credit offsets require Single Invoice Line mode so SAP receives one positive net AR line.',
        severity: 'warning',
      }));
      return;
    }
    const incompleteManualJE = selectedRows.find((row) => row.manualJEEntry && (
      !(Number(row.grpoCost) > 0)
      || !String(row.debitAccountCode || '').trim()
      || !String(row.creditAccountCode || '').trim()
      || ['dim1', 'dim2', 'dim3', 'dim4', 'dim5'].some((field) => !String((row as any)[field] || '').trim())
    ));
    if (incompleteManualJE) {
      const missingManualFields = [
        !(Number(incompleteManualJE.grpoCost) > 0) ? 'amount in Base (LC)' : '',
        !String(incompleteManualJE.debitAccountCode || '').trim() ? 'JE Debit G/L' : '',
        !String(incompleteManualJE.creditAccountCode || '').trim() ? 'JE Credit G/L' : '',
        !String(incompleteManualJE.projectCode || '').trim() ? 'Project' : '',
        !String(incompleteManualJE.dim1 || '').trim() ? 'Division' : '',
        !String(incompleteManualJE.dim2 || '').trim() ? 'Department' : '',
        !String(incompleteManualJE.dim3 || '').trim() ? 'Cost Code' : '',
        !String(incompleteManualJE.dim4 || '').trim() ? 'Location' : '',
        !String(incompleteManualJE.dim5 || '').trim() ? 'Auxiliary' : '',
      ].filter(Boolean);
      dispatch(showSnackbar({
        message: `Manual Input row is missing: ${missingManualFields.join(', ')}`,
        severity: 'warning',
      }));
      return;
    }
    setProcessPreview({
      open: true,
      payload: buildProcessPayload(selectedRows),
      rows: selectedRows.map((row) => ({ ...row })),
    });
  };

  const confirmProcessPreview = async () => {
    const previewRows = processPreview.rows;
    const payload = processPreview.payload || buildProcessPayload(previewRows);
    if (!payload || previewRows.length === 0) {
      setProcessPreview({ open: false, payload: null, rows: [] });
      dispatch(showSnackbar({ message: 'No transactions available to process', severity: 'warning' }));
      return;
    }
    try {
      setProcessPreview((prev) => ({ ...prev, open: false }));
      setProcessing(true);
      dispatch(setLoading(true));
      console.log('[AutoInvoice] process payload', payload);
      console.log('[AutoInvoice] process payload JSON', JSON.stringify(payload, null, 2));
      const res = await apiService.processAutoInvoice(payload);
      const responseMode = res?.data?.processMode || processMode;
      const draftCount = Number(res?.data?.draftCount || (res?.data?.arInvoiceDocNum ? 1 : 0));
      const draftNumbers = Array.isArray(res?.data?.arDrafts)
        ? res.data.arDrafts.map((draft: any) => draft.DocEntry || draft.DocNum).filter(Boolean).join(', ')
        : (res?.data?.arInvoiceDocEntry || res?.data?.arDraftDocEntry || '');
      const unbilledJEs = Array.isArray(res?.data?.unbilledJournalEntries)
        ? res.data.unbilledJournalEntries
            .map((journal: any) => {
              const docNum = journal?.docNum;
              const docEntry = journal?.docEntry;
              if (!docNum && !docEntry) return '';
              return docNum && docEntry && String(docNum) !== String(docEntry)
                ? `${docNum} (entry ${docEntry})`
                : String(docNum || docEntry);
            })
            .filter(Boolean)
        : [];
      const resultRows = Array.isArray(res?.data?.results) ? res.data.results : [];
      const goodsIssues = Array.from(new Set(resultRows
        .map((row: any) => row?.goodsIssueDocNum || row?.goodsIssueDocEntry)
        .filter(Boolean)
        .map(String)));
      const cogsJEs = Array.from(new Set(resultRows
        .map((row: any) => row?.cogsJournalEntryDocNum || row?.cogsJournalEntryDocEntry)
        .filter(Boolean)
        .map(String)));
      const reversalJEs = Array.from(new Set(resultRows
        .map((row: any) => {
          const docNum = row?.reversalJEDocNum;
          const docEntry = row?.reversalJEDocEntry;
          if (!docNum && !docEntry) return '';
          return docNum && docEntry && String(docNum) !== String(docEntry)
            ? `${docNum} (entry ${docEntry})`
            : String(docNum || docEntry);
        })
        .filter(Boolean)));
      const processedCount = Number(res?.data?.processed || previewRows.length);
      const successSummary = responseMode === 'final-unbilled'
        ? [
            ...(draftNumbers ? [{ label: draftCount === 1 ? 'AR draft entry' : 'AR draft entries', value: draftNumbers }] : []),
            ...(reversalJEs.length ? [{ label: reversalJEs.length === 1 ? 'Reversal JE' : 'Reversal JEs', value: reversalJEs.join(', ') }] : []),
          ]
        : [
            ...(unbilledJEs.length ? [{ label: unbilledJEs.length === 1 ? 'Unbilled JE' : 'Unbilled JEs', value: unbilledJEs.join(', ') }] : []),
            ...(goodsIssues.length ? [{ label: goodsIssues.length === 1 ? 'Goods Issue' : 'Goods Issues', value: goodsIssues.join(', ') }] : []),
            ...(cogsJEs.length ? [{ label: cogsJEs.length === 1 ? 'COGS JE' : 'COGS JEs', value: cogsJEs.join(', ') }] : []),
            { label: 'Auxiliary', value: 'SA00007' },
          ];
      setProcessResult({
        open: true,
        severity: 'success',
        title: responseMode === 'final-unbilled' ? 'AR Draft Created' : 'Unbilled Revenue Posted',
        message: responseMode === 'final-unbilled'
          ? `${draftCount || processedCount} AR invoice draft${(draftCount || processedCount) === 1 ? '' : 's'} created successfully.`
          : `${processedCount} transaction${processedCount === 1 ? '' : 's'} posted successfully. No AR draft was created.`,
        summary: successSummary,
        canViewUnbilledJEs: responseMode === 'direct' && unbilledJEs.length > 0,
      });
      setProcessPreview({ open: false, payload: null, rows: [] });
      setKpiDeduction(0);
      await loadGRPOs();
    } catch (error: any) {
      console.error('[AutoInvoice] process failed', {
        status: error?.response?.status,
        response: error?.response?.data,
        message: error?.message,
      });
      console.error('[AutoInvoice] process failed JSON', JSON.stringify({
        status: error?.response?.status,
        response: error?.response?.data,
        message: error?.message,
      }, null, 2));
      setProcessResult({
        open: true,
        severity: 'error',
        title: 'Auto Invoice Failed',
        message: getErrorMessage(error, 'Auto invoice processing failed'),
        details: error?.response?.status ? `HTTP Status: ${error.response.status}` : undefined,
      });
    } finally {
      setProcessing(false);
      dispatch(setLoading(false));
    }
  };

  const updateHistoryFilter = (key: keyof HistoryFilters, value: string) => {
    setHistoryFilters((prev) => ({ ...prev, [key]: value }));
    setHistoryPage(0);
  };

  const getAccountLabel = useCallback((code: unknown): string => {
    const normalized = String(code || '').trim();
    if (!normalized) return '';
    const account = accounts.find((item) => String(item.code || '').toUpperCase() === normalized.toUpperCase());
    return account?.name ? `${normalized} - ${account.name}` : normalized;
  }, [accounts]);

  const buildInlineOptions = useCallback((
    masterOptions: Array<{ code: string; name?: string | null }>,
    rowValues: Array<string | null | undefined>,
  ) => {
    const byCode = new Map<string, GridChooseOption>();
    const addOption = (code: unknown, name?: unknown) => {
      const normalizedCode = String(code || '').trim();
      if (!normalizedCode) return;
      const normalizedName = String(name || '').trim();
      byCode.set(normalizedCode.toUpperCase(), {
        code: normalizedCode,
        name: normalizedName,
        label: normalizedName ? `${normalizedCode} - ${normalizedName}` : normalizedCode,
      });
    };
    masterOptions.forEach((option) => addOption(option.code, option.name));
    rowValues.forEach((value) => addOption(value));
    return Array.from(byCode.values()).sort((a, b) => a.code.localeCompare(b.code));
  }, []);

  const projectInlineOptions = useMemo(
    () => buildInlineOptions(projects, rows.map((row) => row.projectCode)),
    [buildInlineOptions, projects, rows],
  );

  const dimensionInlineOptions = useMemo(() => ({
    dim1: buildInlineOptions(dimensions.dim1, rows.map((row) => row.dim1)),
    dim2: buildInlineOptions(dimensions.dim2, rows.map((row) => row.dim2)),
    dim3: buildInlineOptions(dimensions.dim3, rows.map((row) => row.dim3)),
    dim4: buildInlineOptions(dimensions.dim4, rows.map((row) => row.dim4)),
    dim5: buildInlineOptions(dimensions.dim5, rows.map((row) => row.dim5)),
  }), [buildInlineOptions, dimensions, rows]);

  const accountInlineOptions = useMemo(
    () => buildInlineOptions(accounts, rows.map((row) => row.cogsAccountCode)),
    [accounts, buildInlineOptions, rows],
  );

  const advancedAccountInlineOptions = useMemo(() => {
    const postingDate = today();
    const rules = advancedGLRules.filter((rule) => {
      const inDateRange = (!rule.fromDate || rule.fromDate <= postingDate)
        && (!rule.toDate || rule.toDate >= postingDate);
      return inDateRange;
    });
    const options = rules.map((rule) => {
      const account = accounts.find((item) => item.code.toUpperCase() === rule.costAccount.toUpperCase());
      const context = [rule.udf2, rule.description].map((value) => String(value || '').trim()).filter(Boolean).join(' / ');
      return {
        code: rule.costAccount,
        name: [account?.name, context ? `SAP rule: ${context}` : 'SAP advanced rule'].filter(Boolean).join(' — '),
      };
    });
    return buildInlineOptions(options, rows.map((row) => row.cogsAccountCode));
  }, [accounts, advancedGLRules, buildInlineOptions, rows]);

  const getGridChooseOptions = useCallback((field: GridChooseField): GridChooseOption[] => {
    if (field === 'projectCode') return projectInlineOptions;
    if (field === 'debitAccountCode' || field === 'creditAccountCode') return accountInlineOptions;
    if (field === 'cogsAccountCode') {
      const currentRow = rows.find((row) => row.id === gridChooseDialog.rowId);
      return currentRow?.transactionType === 'Manual JE' ? accountInlineOptions : advancedAccountInlineOptions;
    }
    return dimensionInlineOptions[field];
  }, [accountInlineOptions, advancedAccountInlineOptions, dimensionInlineOptions, gridChooseDialog.rowId, projectInlineOptions, rows]);

  const filteredGridChooseOptions = useMemo(() => {
    const options = getGridChooseOptions(gridChooseDialog.field);
    const term = gridChooseDialog.search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) => (
      String(option.code || '').toLowerCase().includes(term)
      || String(option.name || '').toLowerCase().includes(term)
    ));
  }, [getGridChooseOptions, gridChooseDialog.field, gridChooseDialog.search]);

  const openGridChooseDialog = useCallback((row: GRPORow, field: GridChooseField, title: string) => {
    setGridChooseDialog({
      open: true,
      rowId: row.id,
      field,
      title,
      search: '',
      draftCode: String((row as any)[field] || '').trim(),
    });
  }, []);

  const applyGridChooseSelection = useCallback((code?: string) => {
    const selectedCode = String(code ?? (gridChooseDialog.draftCode || '')).trim();
    if (!gridChooseDialog.rowId) return;
    setRows((prev) => prev.map((row) => (
      row.id === gridChooseDialog.rowId
        ? (() => {
          const nextChosen = { ...(row.userChosenFields || {}) };
          if (selectedCode) {
            nextChosen[gridChooseDialog.field] = true;
          } else {
            delete nextChosen[gridChooseDialog.field];
          }
          return {
          ...row,
          hasMultipleDimensions: gridChooseDialog.field === 'projectCode' ? false : row.hasMultipleDimensions,
          dim5Overridden: gridChooseDialog.field === 'dim5'
            ? true
            : row.dim5Overridden,
          [gridChooseDialog.field]: selectedCode,
          userChosenFields: nextChosen,
        };
        })()
        : row
    )));
    setGridChooseDialog((prev) => ({ ...prev, open: false }));
  }, [gridChooseDialog.draftCode, gridChooseDialog.field, gridChooseDialog.rowId]);

  const renderChooseCell = useCallback((field: GridChooseField, title: string, getLabel: (value: unknown) => string) => (params: any) => {
    if (field === 'cogsAccountCode' && processMode === 'final-unbilled') {
      return <Chip size="small" color="info" variant="outlined" label={params.row?.unbilledAccountCode || unbilledAccountLabel} />;
    }
    const mappedAccounts = field === 'cogsAccountCode' && Array.isArray(params.row?.cogsAccountCodes)
      ? params.row.cogsAccountCodes.filter(Boolean)
      : [];
    const value = getLabel(params.value) || mappedAccounts.join(', ');
    const usesSapAdvancedRule = field === 'cogsAccountCode'
      && !params.row?.userChosenFields?.cogsAccountCode
      && (Boolean(params.value) || mappedAccounts.length > 0);
    const delegatesToSap = field === 'cogsAccountCode'
      && (params.row?.transactionType || 'GRPO') === 'GRPO'
      && !params.value
      && mappedAccounts.length === 0;
    const isUserChosen = Boolean(params.row?.userChosenFields?.[field])
      || (field === 'cogsAccountCode' && Boolean(params.value) && params.row?.invoiceStatus !== 'Pending');
    return (
      <Button
        size="small"
        title={field === 'cogsAccountCode' && params.row?.cogsMappingSource
          ? `Auto-mapped ${mappedAccounts.length > 1 ? 'per line' : ''}: ${params.row.cogsMappingSource}${mappedAccounts.length ? ` (${mappedAccounts.join(', ')})` : ''}. Click to override.`
          : (usesSapAdvancedRule ? 'SAP advanced G/L mapping. Click to override.' : value)}
        variant={isUserChosen ? 'outlined' : 'text'}
        onClick={(event) => {
          event.stopPropagation();
          openGridChooseDialog(params.row as GRPORow, field, title);
        }}
        sx={{
          justifyContent: 'flex-start',
          minWidth: 0,
          maxWidth: '100%',
          px: 0.25,
          py: 0,
          borderColor: isUserChosen ? '#d98b00' : 'transparent',
          bgcolor: isUserChosen ? '#fff3d6' : 'transparent',
          color: isUserChosen ? '#7a4b00' : undefined,
          '&:hover': {
            borderColor: isUserChosen ? '#c47a00' : undefined,
            bgcolor: isUserChosen ? '#ffe7b3' : undefined,
          },
          textTransform: 'none',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
      >
        {mappedAccounts.length > 1
          ? <Chip size="small" color="success" variant="outlined" label={`${mappedAccounts.length} mapped GLs`} />
          : value || (usesSapAdvancedRule
          ? <Chip size="small" color="success" variant="outlined" label="SAP Advanced" />
          : delegatesToSap
            ? <Chip size="small" color="info" variant="outlined" label="SAP Posting" />
          : <Chip size="small" color="warning" variant="outlined" label="Choose" />)}
      </Button>
    );
  }, [openGridChooseDialog, processMode, unbilledAccountLabel]);

  const makeResizableColumns = useCallback((
    baseColumns: GridColDef[],
    widths: Record<string, number>,
  ): GridColDef[] => baseColumns.map((column) => {
    const field = String(column.field);
    const fallbackWidth = Number(column.width || column.minWidth || 120);
    const width = widths[field] || fallbackWidth;
    return {
      ...column,
      width,
      flex: undefined,
      resizable: false,
    };
  }), []);

  const createHeaderResizeMouseDown = useCallback((
    widths: Record<string, number>,
    setWidths: React.Dispatch<React.SetStateAction<Record<string, number>>>,
    baseColumns: GridColDef[],
  ) => (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    const header = target.closest('.MuiDataGrid-columnHeader') as HTMLElement | null;
    if (!header) return;
    const field = header.getAttribute('data-field') || '';
    if (!field || field === '__check__') return;
    const rect = header.getBoundingClientRect();
    const edgeHitWidth = 9;
    if (event.clientX < rect.right - edgeHitWidth) return;

    event.preventDefault();
    event.stopPropagation();

    const column = baseColumns.find((item) => String(item.field) === field);
    const fallbackWidth = Number(column?.width || column?.minWidth || rect.width || 120);
    const startWidth = widths[field] || fallbackWidth;
    const startX = event.clientX;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const nextWidth = Math.max(70, Math.min(700, startWidth + moveEvent.clientX - startX));
      setWidths((prev) => ({ ...prev, [field]: nextWidth }));
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  const handleHeaderResizeMouseMove = useCallback((event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    const header = target.closest('.MuiDataGrid-columnHeader') as HTMLElement | null;
    const container = event.currentTarget as HTMLElement;
    if (!header || header.getAttribute('data-field') === '__check__') {
      container.style.cursor = '';
      return;
    }
    const rect = header.getBoundingClientRect();
    container.style.cursor = event.clientX >= rect.right - 9 ? 'col-resize' : '';
  }, []);

  const handleHeaderResizeMouseLeave = useCallback((event: React.MouseEvent) => {
    (event.currentTarget as HTMLElement).style.cursor = '';
  }, []);

  const exportHistory = async () => {
    if (!companyId) return;
    try {
      dispatch(setLoading(true));
      const res = await apiService.getAutoInvoiceHistory({
        companyId,
        ...historyFilters,
        page: 1,
        limit: 500,
      });
      const rows: HistoryRow[] = res?.data?.rows || [];
      const exportData = rows.map((row) => ({
        RunID: row.runid,
        TransactionType: row.transactiontype || 'GRPO',
        Project: row.projectcode,
        Customer: row.customercode,
        SourceDocEntry: row.sourcedocentry || row.grpodocentry || row.salesorderdocentry,
        SourceDocNum: row.sourcedocnum || row.grpodocnum || row.salesorderdocnum,
        SourceDocDate: row.sourcedocdate || row.grpodocdate,
        GRPODocEntry: row.grpodocentry,
        GRPONumber: row.grpodocnum,
        GRPODate: row.grpodocdate,
        SalesOrderDocEntry: row.salesorderdocentry,
        SalesOrderDocNum: row.salesorderdocnum,
        GRPOCost: Number(row.grpocost || 0),
        SalesPrice: Number(row.salesprice || 0),
        GoodsIssueDocEntry: row.goodsissuedocentry,
        GoodsIssueDocNum: row.goodsissuedocnum,
        COGSAccount: row.cogsaccountcode,
        COGSJournalEntryDocEntry: row.cogsjournalentrydocentry,
        COGSJournalEntryDocNum: row.cogsjournalentrydocnum,
        ARDraftDocEntry: row.arinvoicedocentry,
        ARDraftDocNum: row.arinvoicedocnum,
        Status: row.status,
        InvoiceType: row.invoicetype,
        InvoiceLineMode: row.invoicelinemode,
        ProcessMode: row.processmode,
        DraftGrouping: row.draftgrouping,
        ItemCode: row.itemcode,
        ItemName: row.itemname,
        MarginPercent: row.marginpercent,
        RunCreatedDate: row.runcreateddate,
        RunFinishedDate: row.runfinisheddate,
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Auto Invoice History');
      XLSX.writeFile(wb, `Auto_Invoice_History_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (error: any) {
      dispatch(showSnackbar({ message: getErrorMessage(error, 'Failed to export history'), severity: 'error' }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const baseColumns: GridColDef[] = [
    { field: 'grpoDocNum', headerName: 'GRPO', width: 82 },
    {
      field: 'transactionType',
      headerName: 'Type',
      width: 92,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value || 'GRPO'}
          color={params.value === 'GRPO' ? 'primary' : 'default'}
          variant={params.value === 'GRPO' ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'documentNo',
      headerName: 'Doc No.',
      width: 92,
      renderCell: (params) => (
        params.row?.transactionType === 'Sales Order' ? (
          <Button size="small" variant="text" onClick={() => openSalesOrderHistory(params.row as GRPORow)}>
            {params.value || '-'}
          </Button>
        ) : params.row?.transactionType === 'Manual JE' && !params.row?.manualJEEntry ? (
          <Button size="small" variant="text" onClick={() => openManualJEHistory(params.row as GRPORow)}>
            {params.value || '-'}
          </Button>
        ) : String(params.value || (params.row?.manualJEEntry ? 'Manual Input' : '-'))
      ),
    },
    { field: 'docDate', headerName: 'Date', width: 92 },
    {
      field: 'invoiceStatus',
      headerName: 'Status',
      width: 98,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value}
          color={params.value === 'Invoiced' || params.value === 'Drafted' ? 'success' : params.value === 'Reference' ? 'default' : 'warning'}
          variant={params.value === 'Invoiced' || params.value === 'Drafted' ? 'filled' : 'outlined'}
        />
      ),
    },
    { field: 'cardCode', headerName: 'BP', width: 86 },
    { field: 'cardName', headerName: 'BP Name', minWidth: 145, flex: 1 },
    {
      field: 'projectCode',
      headerName: 'Project',
      width: 128,
      renderCell: (params) => params.row?.hasMultipleDimensions ? (
        <Button
          size="small"
          variant="text"
          color="error"
          onClick={(event) => {
            event.stopPropagation();
            openGridChooseDialog(params.row as GRPORow, 'projectCode', 'Select Project');
          }}
          sx={{ textTransform: 'none' }}
        >
          Multiple
        </Button>
      ) : renderChooseCell('projectCode', 'Select Project', (value) => getLineDefaultOptionLabel('projectCode', value))(params),
    },
    {
      field: 'dim1',
      headerName: dimensionLabels.dim1,
      width: 108,
      renderCell: renderChooseCell('dim1', `Select ${dimensionLabels.dim1}`, (value) => getLineDefaultOptionLabel('dim1', value)),
    },
    {
      field: 'dim2',
      headerName: dimensionLabels.dim2,
      width: 116,
      renderCell: renderChooseCell('dim2', `Select ${dimensionLabels.dim2}`, (value) => getLineDefaultOptionLabel('dim2', value)),
    },
    {
      field: 'dim3',
      headerName: 'Cost Code',
      width: 112,
      renderCell: renderChooseCell('dim3', `Select ${dimensionLabels.dim3}`, (value) => getLineDefaultOptionLabel('dim3', value)),
    },
    {
      field: 'dim4',
      headerName: dimensionLabels.dim4,
      width: 108,
      renderCell: renderChooseCell('dim4', `Select ${dimensionLabels.dim4}`, (value) => getLineDefaultOptionLabel('dim4', value)),
    },
    {
      field: 'dim5',
      headerName: dimensionLabels.dim5,
      width: 108,
      renderCell: renderChooseCell('dim5', `Select ${dimensionLabels.dim5}`, (value) => getLineDefaultOptionLabel('dim5', value)),
    },
    {
      field: 'cogsAccountCode',
      headerName: 'COGS GL',
      width: 124,
      renderCell: (params) => params.row?.manualJEEntry
        ? '-'
        : renderChooseCell('cogsAccountCode', 'COGS GL Account (SAP Advanced)', getAccountLabel)(params),
    },
    processMode === 'final-unbilled' ? {
      field: 'journalEntryDebitAmount',
      headerName: 'JE Debit',
      width: 108,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => params.row?.transactionType === 'Manual JE'
        ? money(params.value, params.row?.currency)
        : '-',
    } : {
      field: 'debitAccountCode',
      headerName: 'JE Debit G/L',
      width: 140,
      renderCell: (params) => params.row?.manualJEEntry
        ? renderChooseCell('debitAccountCode', 'Select JE Debit G/L', getAccountLabel)(params)
        : '-',
    },
    processMode === 'final-unbilled' ? {
      field: 'journalEntryCreditAmount',
      headerName: 'JE Credit',
      width: 108,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => params.row?.transactionType === 'Manual JE'
        ? money(params.value, params.row?.currency)
        : '-',
    } : {
      field: 'creditAccountCode',
      headerName: 'JE Credit G/L',
      width: 140,
      renderCell: (params) => params.row?.manualJEEntry
        ? renderChooseCell('creditAccountCode', 'Select JE Credit G/L', getAccountLabel)(params)
        : '-',
    },
    {
      field: 'journalEntryNetAmount',
      headerName: 'JE Net',
      width: 108,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => params.row?.transactionType === 'Manual JE'
        ? money(params.value, params.row?.currency)
        : '-',
    },
    {
      field: 'journalEntryProjectLineCount',
      headerName: 'JE Lines',
      width: 82,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => params.row?.transactionType === 'Manual JE'
        ? Number(params.value || 0)
        : '-',
    },
    {
      field: 'sourceCurrency',
      headerName: 'Source Curr.',
      width: 102,
      renderCell: (params) => params.value || params.row?.currency || '-',
    },
    {
      field: 'sourceAmount',
      headerName: 'Source Amount',
      width: 122,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => money(params.value, params.row?.sourceCurrency || params.row?.currency),
    },
    {
      field: 'exchangeRate',
      headerName: 'FX to LC',
      width: 92,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => Number(params.value || 0) > 0
        ? Number(params.value).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 6 })
        : '-',
    },
    {
      field: 'grpoCost',
      headerName: processMode === 'final-unbilled' ? 'Remaining (LC)' : 'Base (LC)',
      width: 106,
      align: 'right',
      headerAlign: 'right',
      editable: true,
      renderCell: (params) => params.row?.manualJEEntry && processMode === 'direct' ? (
        <TextField
          size="small"
          type="number"
          value={params.row.manualAmountInput ?? (params.row.grpoCost ? String(params.row.grpoCost) : '')}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => {
            // Negatives are not accepted here, so drop the sign as it is typed
            // rather than displaying -500 while submitting 0.
            const inputValue = event.target.value.replace(/-/g, '');
            const parsedAmount = Number(inputValue);
            const amount = inputValue.trim() === '' || !Number.isFinite(parsedAmount)
              ? 0
              : Math.max(0, parsedAmount);
            setRows((previous) => previous.map((row) => row.id === params.row.id
              ? {
                ...row,
                manualAmountInput: inputValue,
                grpoCost: amount,
                salesPrice: calculateWithMargin(amount, marginPercent),
              }
              : row));
          }}
          inputProps={{ min: 0, step: '0.01', 'aria-label': 'Manual JE amount in base currency' }}
          placeholder="Enter amount"
          sx={{
            width: 118,
            '& .MuiInputBase-root': { height: 28, fontSize: '0.8rem' },
            '& input': { textAlign: 'right', py: 0.25, px: 0.75 },
          }}
        />
      ) : money(params.value, params.row?.currency),
    },
    {
      field: 'salesOrderBilledBaseAmount',
      headerName: processMode === 'final-unbilled' ? 'Previously Finalized' : 'Billed',
      width: processMode === 'final-unbilled' ? 145 : 100,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        if (processMode === 'final-unbilled') {
          return money(params.row?.unbilledFinalizedAmount, params.row?.currency);
        }
        if (params.row?.transactionType === 'Sales Order') {
          return money(params.value, params.row?.currency);
        }
        if (params.row?.transactionType === 'Manual JE') {
          return money(params.row?.journalEntryBilledBaseAmount, params.row?.currency);
        }
        return '-';
      },
    },
    {
      field: 'salesOrderBillingCount',
      headerName: 'Bills',
      width: 85,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        if (params.row?.transactionType === 'Sales Order') {
          const count = Number(params.value || 0) || (params.row?.arInvoiceDocNum ? 1 : 0);
          return <Button size="small" variant="outlined" onClick={() => openSalesOrderHistory(params.row as GRPORow)}>{count}</Button>;
        }
        if (params.row?.transactionType === 'Manual JE') {
          if (params.row?.manualJEEntry) return '-';
          const count = Number(params.row?.journalEntryBillingCount || 0) || (params.row?.arInvoiceDocNum ? 1 : 0);
          return <Button size="small" variant="outlined" onClick={() => openManualJEHistory(params.row as GRPORow)}>{count}</Button>;
        }
        return '-';
      },
    },
    {
      field: 'salesPrice',
      headerName: processMode === 'final-unbilled' ? 'Invoice Amount' : 'Before KPI',
      width: processMode === 'final-unbilled' ? 130 : 104,
      align: 'right',
      headerAlign: 'right',
      editable: processMode === 'final-unbilled',
      cellClassName: processMode === 'final-unbilled' ? 'auto-invoice-editable-amount' : '',
      renderCell: (params) => money(params.value, params.row?.currency),
    },
    {
      field: 'kpiAllocation',
      headerName: 'KPI',
      width: 92,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      renderCell: (params) => {
        const allocation = rowKpiAllocation.get(params.row.id);
        return allocation ? money(allocation.kpi, params.row?.currency) : '-';
      },
    },
    {
      field: 'finalArValue',
      headerName: 'Final AR',
      width: 104,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      renderCell: (params) => {
        const allocation = rowKpiAllocation.get(params.row.id);
        return allocation ? money(allocation.final, params.row?.currency) : '-';
      },
    },
    { field: 'arInvoiceDocNum', headerName: 'AR Draft', width: 92 },
    { field: 'goodsIssueDocNum', headerName: 'GI', width: 82 },
    { field: 'cogsJournalEntryDocNum', headerName: 'COGS JE', width: 92 },
  ];
  const columns: GridColDef[] = makeResizableColumns(baseColumns.filter((column) => processMode === 'direct'
    ? !['journalEntryNetAmount', 'journalEntryProjectLineCount'].includes(String(column.field))
    : !['salesOrderBillingCount', 'kpiAllocation', 'finalArValue', 'goodsIssueDocNum', 'cogsJournalEntryDocNum', 'cogsAccountCode']
      .includes(String(column.field))), processColumnWidths);

  const historyColumns: GridColDef[] = makeResizableColumns([
    { field: 'runid', headerName: 'Run', width: 64 },
    {
      field: 'transactiontype',
      headerName: 'Type',
      width: 92,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value || 'GRPO'}
          color={(params.value || 'GRPO') === 'GRPO' ? 'primary' : 'default'}
          variant={(params.value || 'GRPO') === 'GRPO' ? 'filled' : 'outlined'}
        />
      ),
    },
    { field: 'projectcode', headerName: 'Project', width: 112 },
    { field: 'customercode', headerName: 'Customer', width: 96 },
    { field: 'sourcedocnum', headerName: 'Source', width: 88 },
    { field: 'sourcedocdate', headerName: 'Src Date', width: 98 },
    {
      field: 'grpocost',
      headerName: 'Base',
      width: 96,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => money(params.value),
    },
    {
      field: 'salesprice',
      headerName: 'AR Value',
      width: 98,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => money(params.value),
    },
    { field: 'goodsissuedocnum', headerName: 'GI', width: 78 },
    { field: 'cogsaccountcode', headerName: 'COGS GL', width: 106 },
    { field: 'cogsjournalentrydocnum', headerName: 'COGS JE', width: 88 },
    { field: 'arinvoicedocnum', headerName: 'AR Draft', width: 90 },
    { field: 'status', headerName: 'Status', width: 88 },
    { field: 'processmode', headerName: 'Process', width: 112 },
    { field: 'draftgrouping', headerName: 'Draft Output', width: 105 },
    { field: 'invoicetype', headerName: 'Sales Type', width: 112 },
    { field: 'invoicelinemode', headerName: 'Mode', width: 82 },
    { field: 'itemcode', headerName: 'Item', width: 86 },
    { field: 'itemname', headerName: 'Item Name', width: 130 },
    { field: 'runcreateddate', headerName: 'Run Date', width: 138 },
  ], historyColumnWidths);

  const erpGridSx = {
    flex: 1,
    minHeight: { xs: 460, md: 0 },
    minWidth: 0,
    border: '1px solid #dfe7ef',
    borderRadius: 1,
    overflow: 'hidden',
    '& .MuiDataGrid-main': {
      backgroundColor: '#fff',
    },
    fontSize: 11,
    '& .MuiDataGrid-cell': { outline: 'none', py: 0, px: 0.5, fontSize: 11, lineHeight: 1.15 },
    '& .MuiDataGrid-columnHeader': { py: 0, px: 0.5, fontSize: 11, lineHeight: 1.15 },
    '& .MuiDataGrid-columnHeaderTitle': { fontSize: 11, fontWeight: 700 },
    '& .MuiChip-root': { height: 20, fontSize: 10.5 },
    '& .MuiButton-root': { minHeight: 22, fontSize: 10.5, px: 0.6 },
    '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeaderCheckbox)::after': {
      content: '""',
      position: 'absolute',
      right: -5,
      top: 0,
      bottom: 0,
      width: 10,
      borderLeft: '2px solid rgba(154, 168, 182, 0.55)',
      backgroundColor: 'transparent',
      backgroundClip: 'content-box',
      cursor: 'col-resize',
      pointerEvents: 'auto',
      zIndex: 4,
    },
    '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeaderCheckbox):hover::after': {
      borderLeftColor: '#0a6ed1',
    },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: '#f3f7fb',
      borderBottom: '1px solid #d5e0ea',
    },
    '& .MuiDataGrid-row.Mui-selected': {
      backgroundColor: 'rgba(10, 110, 209, 0.08)',
    },
    '& .MuiDataGrid-cellCheckbox, & .MuiDataGrid-columnHeaderCheckbox': {
      minWidth: '34px !important',
      maxWidth: '34px !important',
      width: '34px !important',
    },
  };

  const footerBaseSx = {
    minHeight: 'auto',
    px: { xs: 0.75, md: 1 },
    py: 0.75,
    gap: 0.75,
    borderTop: '1px solid #d9e2ea',
    background: 'linear-gradient(180deg, #ffffff 0%, #f5f8fb 100%)',
    boxShadow: '0 -2px 8px rgba(26, 44, 64, 0.06)',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    '& .MuiTablePagination-root': {
      overflow: 'visible',
    },
    '& .MuiTablePagination-toolbar': {
      minHeight: 32,
      pl: 0,
      pr: 0,
    },
    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
      fontSize: 12,
      my: 0,
    },
  };

  const GrpoWorkbenchFooter = () => (
    <GridFooterContainer sx={footerBaseSx}>
      <Stack
        direction="row"
        spacing={0.75}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        sx={{ minWidth: 0, flex: '1 1 520px' }}
      >
        <Chip size="small" color={selectedRows.length ? 'primary' : 'default'} label={`Selected ${selectedRows.length}`} />
        <Chip size="small" variant="outlined" label={`Showing ${filteredRows.length} / ${rows.length}`} />
        {processMode === 'direct' && <Chip size="small" variant="outlined" label={`GRPO ${rows.filter((row) => row.transactionType === 'GRPO' || !row.transactionType).length}`} />}
        {processMode === 'direct' && <Chip size="small" variant="outlined" label={`SO ${rows.filter((row) => row.transactionType === 'Sales Order').length}`} />}
        <Chip size="small" variant="outlined" label={`${processMode === 'final-unbilled' ? 'Unbilled JE' : 'JE'} ${rows.filter((row) => row.transactionType === 'Manual JE').length}`} />
        <Chip size="small" variant="outlined" label={`Base ${money(totals.cost)}`} />
        <Chip size="small" variant="outlined" label={`${processMode === 'final-unbilled' ? 'Unbilled Net' : 'Before KPI'} ${money(totals.sales)}`} />
        {processMode === 'direct' && <Chip size="small" variant="outlined" label={`After KPI ${money(adjustedTotals.netAfterKpi)}`} />}
        <Chip size="small" color="success" variant="outlined" label={`AR ${money(adjustedTotals.arValue)}`} />
        <Chip size="small" variant="outlined" label="System value" sx={{ bgcolor: '#fff' }} />
        <Chip size="small" variant="outlined" label="User selected" sx={{ bgcolor: '#fff3d6', borderColor: '#d98b00', color: '#7a4b00' }} />
        <Chip
          size="small"
          variant="outlined"
          label={processMode === 'final-unbilled'
            ? `${draftGrouping === 'separate' ? 'Separate drafts' : 'Combined draft'} | ${invoiceLineMode === 'single' ? 'Single line' : 'Multi line'}`
            : `${invoiceLineMode === 'single' ? 'Single line' : 'Multi line'} | ${invoiceItems[invoiceType].itemCode}`}
          sx={{ maxWidth: 190, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
        />
      </Stack>

      <Stack
        direction="row"
        spacing={0.75}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
        sx={{ flex: '1 1 560px' }}
      >
        <Button size="small" variant="outlined" startIcon={<SearchIcon />} onClick={loadGRPOs} disabled={loadingRows || processing}>
          Load
        </Button>
        <Button size="small" variant="text" startIcon={<RefreshIcon />} onClick={loadGRPOs} disabled={!rows.length || loadingRows || processing}>
          Refresh
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={processing ? <CircularProgress color="inherit" size={14} /> : <PlayArrowIcon />}
          onClick={processSelected}
          disabled={selectedRows.length === 0 || processing}
          sx={{ minWidth: 112 }}
        >
          {processing ? 'Processing' : (processMode === 'final-unbilled' ? 'Create Final Draft' : 'Process')}
        </Button>
        <Box sx={{ ml: { md: 'auto' } }}>
          <GridPagination />
        </Box>
      </Stack>
    </GridFooterContainer>
  );

  const HistoryWorkbenchFooter = () => (
    <GridFooterContainer sx={footerBaseSx}>
      <Stack
        direction="row"
        spacing={0.75}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        sx={{ minWidth: 0, flex: '1 1 420px' }}
      >
        <Chip size="small" color="primary" variant="outlined" label={`Records ${historyTotal}`} />
        <Chip size="small" variant="outlined" label={`Page ${historyPage + 1}`} />
        <Chip size="small" variant="outlined" label={`Loaded ${historyRows.length}`} />
        <Chip size="small" variant="outlined" label={`From ${formatDisplayDate(historyFilters.fromDate)}`} />
        <Chip size="small" variant="outlined" label={`To ${formatDisplayDate(historyFilters.toDate)}`} />
      </Stack>
      <Stack
        direction="row"
        spacing={0.75}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
        sx={{ flex: '1 1 500px' }}
      >
        <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={loadHistory} disabled={historyLoading}>
          Refresh
        </Button>
        <Button size="small" variant="contained" startIcon={<DownloadIcon />} onClick={exportHistory} disabled={historyLoading || historyTotal === 0}>
          Export Excel
        </Button>
        <Box sx={{ ml: { md: 'auto' } }}>
          <GridPagination />
        </Box>
      </Stack>
    </GridFooterContainer>
  );

  const selectedTypeCounts = {
    grpo: selectedRows.filter((row) => row.transactionType === 'GRPO' || !row.transactionType).length,
    salesOrder: selectedRows.filter((row) => row.transactionType === 'Sales Order').length,
    manualJE: selectedRows.filter((row) => row.transactionType === 'Manual JE').length,
  };

  return (
    <Box
      sx={{
        p: { xs: 1, md: 1.25 },
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        minWidth: 0,
        overflow: 'hidden',
        height: { xs: 'auto', md: 'calc(100vh - 112px)' },
      }}
    >
      <Paper variant="outlined" sx={{ px: 1, py: 0.5, flexShrink: 0 }}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={0.75}
          alignItems={{ xs: 'stretch', lg: 'center' }}
          sx={{ minWidth: 0 }}
        >
          <Tabs value={activeTab} onChange={(_event, value) => setActiveTab(value)} sx={{ minHeight: 36, flexShrink: 0 }}>
            <Tab label="Process" sx={{ minHeight: 36, py: 0.5 }} />
            <Tab label="History" sx={{ minHeight: 36, py: 0.5 }} />
          </Tabs>
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
            sx={{ minWidth: 0, flex: 1 }}
          >
            <ReceiptLongIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2" sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>Auto Invoice</Typography>
            <Chip
              size="small"
              label={`${processMode === 'final-unbilled' ? 'Final AR' : 'Unbilled'} | ${invoiceLineMode === 'single' ? 'Single line' : 'Multi line'}`}
              variant="outlined"
              sx={{ height: 24, maxWidth: { xs: '100%', md: 260 }, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
            />
            {processMode === 'direct' && <Chip size="small" label={`Margin ${money(marginPercent).replace('.00', '')}%`} variant="outlined" sx={{ height: 24 }} />}
            {processMode === 'final-unbilled' && <Chip size="small" color="info" label={`Unbilled GL ${unbilledAccountLabel}`} variant="outlined" sx={{ height: 24 }} />}
            <Chip size="small" label={`Projects ${selectedProjectCodes.length}`} variant="outlined" sx={{ height: 24 }} />
            {activeTab === 1 && <Chip size="small" label={`History ${historyTotal}`} variant="outlined" sx={{ height: 24 }} />}
          </Stack>
          <TextField
            size="small"
            value={activeTab === 0 ? gridSearch : historyFilters.q}
            onChange={(event) => {
              if (activeTab === 0) {
                setGridSearch(event.target.value);
              } else {
                updateHistoryFilter('q', event.target.value);
              }
            }}
            placeholder={activeTab === 0
              ? 'Search GRPO, SO, JE, vendor, project...'
              : 'Search history, AR draft, source doc...'}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" color="disabled" />,
            }}
            sx={{
              width: { xs: '100%', lg: 360 },
              flexShrink: 0,
              '& .MuiInputBase-root': { height: 34 },
              '& .MuiInputBase-input': { py: '6px', fontSize: 12.5 },
            }}
          />
        </Stack>
      </Paper>
      {activeTab === 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            minHeight: 0,
            minWidth: 0,
            flex: 1,
            overflow: 'hidden',
          }}
        >
      <Paper variant="outlined" sx={{ p: { xs: 0.75, md: 1 }, borderRadius: 1, flexShrink: 0 }}>
        <Stack spacing={0.75}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(3, minmax(0, 1fr))',
                lg: 'repeat(5, minmax(0, 1fr))',
                xl: 'repeat(6, minmax(0, 1fr))',
              },
              gap: 0.75,
              alignItems: 'start',
              '& > *': { minWidth: 0 },
              '& .MuiInputBase-root': { minHeight: 34 },
              '& .MuiInputBase-input': { py: '6px', fontSize: 12.5 },
              '& .MuiInputLabel-root': { fontSize: 12 },
            }}
          >
            <TextField
              select
              fullWidth
              size="small"
              label="Operation"
              value={processMode}
              onChange={(event) => {
                const next = event.target.value as ProcessMode;
                setProcessMode(next);
                // The picker is only rendered in direct mode, so carrying it over
                // would override every Final AR row's auxiliary with no way to clear it.
                setHeaderAuxiliaryCode('');
                setDraftGrouping('combined');
                setLoadedItemFilter('All');
                setRows([]);
                setSelection([]);
              }}
            >
              <MenuItem value="direct">Post Unbilled Revenue</MenuItem>
              <MenuItem value="final-unbilled">Final AR Invoice Draft</MenuItem>
            </TextField>
            <Select
              fullWidth
              size="small"
              value={invoiceType}
              displayEmpty
              onChange={(e) => setInvoiceType(e.target.value as InvoiceType)}
            >
              <MenuItem value="charge-back">Charge Back</MenuItem>
              <MenuItem value="special-project">Special Project</MenuItem>
            </Select>
            <Select
              fullWidth
              size="small"
              value={invoiceLineMode}
              displayEmpty
              onChange={(e) => setInvoiceLineMode(e.target.value as InvoiceLineMode)}
            >
              <MenuItem value="single">Single Invoice Line</MenuItem>
              <MenuItem value="multiple">Multiple Invoice Lines</MenuItem>
            </Select>
            <TextField
              select
              fullWidth
              size="small"
              label="Draft Output"
              value={draftGrouping}
              disabled={processMode !== 'final-unbilled'}
              onChange={(event) => setDraftGrouping(event.target.value as DraftGrouping)}
              helperText={processMode === 'final-unbilled' ? 'Combine all selected JEs or create one draft per JE' : 'Used for final AR only'}
            >
              <MenuItem value="combined">Single Invoice Draft</MenuItem>
              <MenuItem value="separate">Multiple Invoice Drafts</MenuItem>
            </TextField>
            <TextField
              size="small"
              label="Projects"
              value={selectedProjectLabel}
              placeholder="Select one or more projects"
              InputProps={{ readOnly: true }}
              onClick={openProjectDialog}
              sx={{ minWidth: 0, cursor: 'pointer', '& .MuiInputBase-root': { cursor: 'pointer' } }}
            />
            <TextField
              size="small"
              label="AR Project"
              value={arHeaderProjectLabel}
              placeholder="Select header project"
              InputProps={{ readOnly: true }}
              onClick={() => {
                if (selectedProjectCodes.length === 0) {
                  dispatch(showSnackbar({ message: 'Select projects first', severity: 'warning' }));
                  return;
                }
                setArHeaderProjectSearch('');
                setDraftArHeaderProjectCode(arHeaderProjectCode);
                setArHeaderProjectDialogOpen(true);
              }}
              sx={{ minWidth: 0, cursor: 'pointer', '& .MuiInputBase-root': { cursor: 'pointer' } }}
            />
            <TextField
              size="small"
              label="Customer"
              value={selectedCustomerLabel}
              placeholder="Choose customer"
              InputProps={{ readOnly: true }}
              onClick={openCustomerDialog}
              sx={{ minWidth: 0, cursor: 'pointer', '& .MuiInputBase-root': { cursor: 'pointer' } }}
            />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="From"
                format="dd/MM/yyyy"
                value={parseIsoDate(fromDate)}
                onChange={(value) => setFromDate(toIsoDate(value))}
                slotProps={{ textField: { size: 'small', fullWidth: true, InputLabelProps: { shrink: true }, sx: { minWidth: 0 } } }}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="To"
                format="dd/MM/yyyy"
                value={parseIsoDate(toDate)}
                onChange={(value) => setToDate(toIsoDate(value))}
                slotProps={{ textField: { size: 'small', fullWidth: true, InputLabelProps: { shrink: true }, sx: { minWidth: 0 } } }}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Posting Date"
                format="dd/MM/yyyy"
                value={parseIsoDate(postingDate)}
                onChange={(value) => setPostingDate(toIsoDate(value))}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    InputLabelProps: { shrink: true },
                    helperText: 'Posting date of the JE and AR draft',
                    sx: { minWidth: 0 },
                  },
                }}
              />
            </LocalizationProvider>
            <TextField
              size="small"
              label="Margin %"
              type="number"
              value={marginPercent}
              inputProps={{ min: 0, step: '0.01' }}
              helperText={marginOverridden ? 'Applied to this invoice' : '0 = no margin; enter only when required'}
              disabled={processMode === 'final-unbilled'}
              onChange={(e) => {
                setMarginOverridden(true);
                setMarginPercent(Math.max(0, Number(e.target.value || 0)));
              }}
            />
            <TextField
              size="small"
              label="KPI Deduction"
              type="number"
              value={kpiDeduction}
              onChange={(e) => setKpiDeduction(Math.max(0, Number(e.target.value || 0)))}
              disabled={processMode === 'final-unbilled'}
            />
            {processMode === 'direct' && (
              <TextField
                select
                size="small"
                label="Auxiliary (header default)"
                value={headerAuxiliaryCode}
                onChange={(event) => {
                  const code = String(event.target.value || '').trim();
                  setHeaderAuxiliaryCode(code);
                  setRows((previous) => previous.map((row) => {
                    const sourceDim5 = String(row.sourceDim5 ?? row.dim5 ?? '').trim();
                    const userChosenFields = { ...(row.userChosenFields || {}) };
                    if (code) userChosenFields.dim5 = true;
                    else delete userChosenFields.dim5;
                    return {
                      ...row,
                      sourceDim5,
                      dim5: code || sourceDim5,
                      dim5Overridden: false,
                      userChosenFields,
                    };
                  }));
                }}
              >
                <MenuItem value="">Select auxiliary</MenuItem>
                {dimensions.dim5.map((option) => (
                  <MenuItem key={option.code} value={option.code}>
                    {getLineDefaultOptionLabel('dim5', option.code)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Box>
        </Stack>
      </Paper>

      <Paper
        variant="outlined"
        onMouseDown={createHeaderResizeMouseDown(processColumnWidths, setProcessColumnWidths, columns)}
        onMouseMove={handleHeaderResizeMouseMove}
        onMouseLeave={handleHeaderResizeMouseLeave}
        sx={{
          p: { xs: 1, md: 1.25 },
          borderRadius: 1,
          minWidth: 0,
          minHeight: 0,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          overflow: 'hidden',
        }}
      >
        {!companyId && <Alert severity="warning" sx={{ mb: 1 }}>Select a company first.</Alert>}
        {processMode === 'final-unbilled' && (
          <Alert severity="info" sx={{ mb: 0.5, py: 0.25 }}>
            Edit <strong>Invoice Amount</strong> for a partial draft. The unused balance remains available for the next invoice.
          </Alert>
        )}
        {processMode === 'direct' && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
              Loaded items
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={loadedItemFilter}
                onChange={(_event, value: LoadedItemFilter | null) => {
                  if (!value) return;
                  setLoadedItemFilter(value);
                  setSelection([]);
                }}
                aria-label="Filter loaded transaction type"
                sx={{ whiteSpace: 'nowrap', '& .MuiToggleButton-root': { px: 1.5, py: 0.45, textTransform: 'none' } }}
              >
                <ToggleButton value="GRPO">GRPO ({loadedItemCounts.GRPO})</ToggleButton>
                <ToggleButton value="Sales Order">Sales Order ({loadedItemCounts['Sales Order']})</ToggleButton>
                <ToggleButton value="Manual JE">JE ({loadedItemCounts['Manual JE']})</ToggleButton>
                <ToggleButton value="All">All ({loadedItemCounts.All})</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Stack>
        )}
        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={loadingRows}
          checkboxSelection
          pagination
          density="compact"
          rowHeight={30}
          columnHeaderHeight={32}
          disableRowSelectionOnClick
          isCellEditable={(params) => (
            (processMode === 'final-unbilled' && params.field === 'salesPrice' && !!params.row.isProcessable)
            || (
              processMode === 'direct'
              && (
                ((params.row.transactionType === 'GRPO' || !params.row.transactionType) && params.row.invoiceStatus === 'Pending')
                || (params.row.transactionType === 'Sales Order' && Number(params.row.salesOrderRemainingAmount ?? params.row.grpoCost) > 0)
                || (params.row.transactionType === 'Manual JE' && !!params.row.isProcessable)
              )
              && params.field === 'grpoCost'
              && (params.row.transactionType === 'Sales Order' || (params.row.transactionType === 'Manual JE' && (params.row.manualJEEntry || params.row.journalEntryDocNum)))
            )
          )}
          isRowSelectable={(params) => (
            params.row.isProcessable === true
            || ((params.row.transactionType === 'GRPO' || !params.row.transactionType) && params.row.invoiceStatus === 'Pending')
            || (params.row.transactionType === 'Sales Order' && Number(params.row.salesOrderRemainingAmount ?? params.row.grpoCost) > 0)
            || (params.row.transactionType === 'Manual JE' && !!params.row.isProcessable
                // Not selectable until it is filled in, so "select all" is usable.
                && (!params.row.manualJEEntry || Number(params.row.grpoCost) > 0))
          )}
          rowSelectionModel={selection}
          onRowSelectionModelChange={(model) => {
            const pendingIds = new Set(rows
              .filter((row) => (
                row.isProcessable === true
                || ((row.transactionType === 'GRPO' || !row.transactionType) && row.invoiceStatus === 'Pending')
                || (row.transactionType === 'Sales Order' && Number(row.salesOrderRemainingAmount ?? row.grpoCost) > 0)
                || (row.transactionType === 'Manual JE' && !!row.isProcessable
                    && (!row.manualJEEntry || Number(row.grpoCost) > 0))
              ))
              .map((row) => row.id));
            setSelection(model.filter((id) => pendingIds.has(id)));
          }}
          processRowUpdate={(updated, original) => {
            // An unreadable entry ("1,000", "-5", blank) used to become NaN, reach
            // the backend as 0, and be read there as "bill the whole remaining
            // amount". Keep the previous value instead and say so.
            const editedCost = Number(String(updated.grpoCost ?? '').trim());
            const costIsUsable = Number.isFinite(editedCost) && editedCost >= 0;
            if (!costIsUsable) {
              dispatch(showSnackbar({
                message: 'Enter a valid amount in Base (LC) — use digits only, no thousands separator.',
                severity: 'warning',
              }));
            }
            const safeCost = costIsUsable ? editedCost : Math.max(0, Number(original?.grpoCost || 0));
            const normalized = {
              ...updated,
              hasMultipleDimensions: false,
              projectCode: String(updated.projectCode || '').trim(),
              dim1: String(updated.dim1 || '').trim(),
              dim2: String(updated.dim2 || '').trim(),
              dim3: String(updated.dim3 || '').trim(),
              dim4: String(updated.dim4 || '').trim(),
              dim5: String(updated.dim5 || '').trim(),
              cogsAccountCode: String(updated.cogsAccountCode || '').trim(),
              debitAccountCode: String(updated.debitAccountCode || '').trim(),
              creditAccountCode: String(updated.creditAccountCode || '').trim(),
              userChosenFields: updated.userChosenFields || {},
              grpoCost: safeCost,
              manualAmountInput: updated.manualJEEntry ? String(safeCost || '') : updated.manualAmountInput,
              salesPrice: processMode === 'final-unbilled'
                ? Math.max(0, Number(updated.salesPrice || 0))
                : updated.transactionType === 'Sales Order' || updated.transactionType === 'Manual JE'
                ? calculateWithMargin(safeCost, marginPercent)
                : Math.max(0, Number(updated.salesPrice || 0)),
            };
            setRows((prev) => prev.map((row) => row.id === normalized.id ? normalized : row));
            return normalized;
          }}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[10, 25, 50, 100]}
          slots={{ footer: GrpoWorkbenchFooter }}
          sx={{
            flex: 1,
            minHeight: { xs: 460, md: 0 },
            minWidth: 0,
            border: '1px solid #dfe7ef',
            '& .auto-invoice-editable-amount': {
              bgcolor: '#fff3d6',
              color: '#7a4b00',
              fontWeight: 800,
              cursor: 'text',
              border: '1px solid #e0a020',
            },
            borderRadius: 1,
            overflow: 'hidden',
            '& .MuiDataGrid-main': {
              backgroundColor: '#fff',
            },
            '& .MuiDataGrid-cell': { outline: 'none', py: 0.25 },
            '& .MuiDataGrid-columnHeader': { py: 0.25 },
            '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeaderCheckbox)::after': {
              content: '""',
              position: 'absolute',
              right: -5,
              top: 0,
              bottom: 0,
              width: 10,
              borderLeft: '2px solid rgba(154, 168, 182, 0.55)',
              backgroundColor: 'transparent',
              backgroundClip: 'content-box',
              cursor: 'col-resize',
              pointerEvents: 'auto',
              zIndex: 4,
            },
            '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeaderCheckbox):hover::after': {
              borderLeftColor: '#0a6ed1',
            },
            '& .MuiDataGrid-columnSeparator': {
              display: 'none',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f3f7fb',
              borderBottom: '1px solid #d5e0ea',
            },
            '& .MuiDataGrid-row.Mui-selected': {
              backgroundColor: 'rgba(10, 110, 209, 0.08)',
            },
            '& .MuiDataGrid-cellCheckbox, & .MuiDataGrid-columnHeaderCheckbox': {
              minWidth: '44px !important',
              maxWidth: '44px !important',
              width: '44px !important',
            },
          }}
        />
      </Paper>
        </Box>
      )}
      {activeTab === 1 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            minHeight: 0,
            minWidth: 0,
            flex: 1,
            overflow: 'hidden',
          }}
        >
          <Paper variant="outlined" sx={{ p: { xs: 0.75, md: 1 }, borderRadius: 1, flexShrink: 0 }}>
            <Stack spacing={0.75}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(3, minmax(0, 1fr))',
                  md: 'minmax(140px, 0.8fr) minmax(130px, 0.7fr) 115px 145px 150px 150px',
                },
                gap: 0.75,
                '& .MuiInputBase-root': { minHeight: 34 },
                '& .MuiInputBase-input': { py: '6px', fontSize: 12.5 },
                '& .MuiInputLabel-root': { fontSize: 12 },
              }}
            >
              <TextField size="small" label="Project" value={historyFilters.projectCode} onChange={(e) => updateHistoryFilter('projectCode', e.target.value)} />
              <TextField size="small" label="Customer" value={historyFilters.customerCode} onChange={(e) => updateHistoryFilter('customerCode', e.target.value)} />
              <TextField size="small" label="AR DocNum" value={historyFilters.arDocNum} onChange={(e) => updateHistoryFilter('arDocNum', e.target.value)} />
              <TextField size="small" label="Goods Issue DocNum" value={historyFilters.goodsIssueDocNum} onChange={(e) => updateHistoryFilter('goodsIssueDocNum', e.target.value)} />
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="From"
                  format="dd/MM/yyyy"
                  value={parseIsoDate(historyFilters.fromDate)}
                  onChange={(value) => updateHistoryFilter('fromDate', toIsoDate(value))}
                  slotProps={{ textField: { size: 'small', fullWidth: true, InputLabelProps: { shrink: true } } }}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="To"
                  format="dd/MM/yyyy"
                  value={parseIsoDate(historyFilters.toDate)}
                  onChange={(value) => updateHistoryFilter('toDate', toIsoDate(value))}
                  slotProps={{ textField: { size: 'small', fullWidth: true, InputLabelProps: { shrink: true } } }}
                />
              </LocalizationProvider>
            </Box>
            </Stack>
          </Paper>

          <Paper
            variant="outlined"
            onMouseDown={createHeaderResizeMouseDown(historyColumnWidths, setHistoryColumnWidths, historyColumns)}
            onMouseMove={handleHeaderResizeMouseMove}
            onMouseLeave={handleHeaderResizeMouseLeave}
            sx={{
              p: { xs: 1, md: 1.25 },
              borderRadius: 1,
              minWidth: 0,
              minHeight: 0,
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
              overflow: 'hidden',
            }}
          >
            <DataGrid
              rows={historyRows}
              columns={historyColumns}
              loading={historyLoading}
              paginationMode="server"
              rowCount={historyTotal}
              paginationModel={{ page: historyPage, pageSize: historyPageSize }}
              onPaginationModelChange={(model) => {
                setHistoryPage(model.page);
                setHistoryPageSize(model.pageSize);
              }}
              pageSizeOptions={[10, 25, 50, 100]}
              density="compact"
              rowHeight={30}
              columnHeaderHeight={32}
              slots={{ footer: HistoryWorkbenchFooter }}
              sx={erpGridSx}
            />
          </Paper>
        </Box>
      )}
      <Dialog open={projectDialogOpen} onClose={() => setProjectDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          Auto Invoice Header Project Selection
          <Typography variant="body2" color="text.secondary">
            Select one or multiple projects. The selected project list is used to load GRPOs.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 1.25, pt: '8px !important' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <TextField
              size="small"
              value={projectDialogSearch}
              onChange={(event) => setProjectDialogSearch(event.target.value)}
              placeholder="Search project code or name..."
              sx={{ minWidth: { xs: '100%', sm: 320 } }}
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="small" label={`Selected ${draftProjectCodes.length}`} />
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  const api = projectGridRef.current?.api;
                  if (!api) return;
                  api.forEachNodeAfterFilter((node) => node.setSelected(true));
                }}
              >
                Select Filtered
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  projectGridRef.current?.api?.deselectAll();
                  setDraftProjectCodes([]);
                }}
              >
                Clear
              </Button>
            </Stack>
          </Stack>
          <Box className="ag-theme-alpine" sx={{ height: { xs: 420, md: 520 }, width: '100%' }}>
            <AgGridReact<ProjectOption>
              ref={projectGridRef}
              rowData={filteredProjectOptions}
              columnDefs={[
                { field: 'code', headerName: 'Project Code', checkboxSelection: true, headerCheckboxSelection: true, width: 170 },
                { field: 'name', headerName: 'Project Name', flex: 1, minWidth: 260 },
              ]}
              defaultColDef={{ sortable: true, filter: true, resizable: true }}
              rowSelection="multiple"
              suppressRowClickSelection={false}
              rowMultiSelectWithClick
              getRowId={(params) => String(params.data.code)}
              onGridReady={syncProjectGridSelection}
              onFirstDataRendered={syncProjectGridSelection}
              onSelectionChanged={() => {
                const selectedCodes = projectGridRef.current?.api
                  ?.getSelectedRows()
                  .map((row) => String(row.code || '').trim())
                  .filter(Boolean) || [];
                setDraftProjectCodes(Array.from(new Set(selectedCodes)));
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProjectDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={applyProjectSelection} disabled={draftProjectCodes.length === 0}>
            Apply {draftProjectCodes.length ? `(${draftProjectCodes.length})` : ''}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={gridChooseDialog.open} onClose={() => setGridChooseDialog((prev) => ({ ...prev, open: false }))} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          {gridChooseDialog.title}
          <Typography variant="body2" color="text.secondary">
            Search and choose by code or name. The selected code will be updated on the current grid row.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 1.25, pt: '8px !important' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <TextField
              size="small"
              value={gridChooseDialog.search}
              onChange={(event) => setGridChooseDialog((prev) => ({ ...prev, search: event.target.value }))}
              placeholder="Search code or name..."
              sx={{ minWidth: { xs: '100%', sm: 400 } }}
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="small" label={gridChooseDialog.draftCode ? `Selected ${gridChooseDialog.draftCode}` : 'No value selected'} />
              <Button
                size="small"
                variant="text"
                onClick={() => applyGridChooseSelection('')}
              >
                Clear
              </Button>
            </Stack>
          </Stack>
          <Box className="ag-theme-alpine" sx={{ height: { xs: 380, md: 500 }, width: '100%' }}>
            <AgGridReact<GridChooseOption>
              rowData={filteredGridChooseOptions}
              columnDefs={[
                { field: 'code', headerName: 'Code', checkboxSelection: true, width: 180 },
                { field: 'name', headerName: 'Name', flex: 1, minWidth: 280 },
              ]}
              defaultColDef={{ sortable: true, filter: true, resizable: true }}
              rowSelection="single"
              suppressRowClickSelection={false}
              getRowId={(params) => String(params.data.code)}
              onFirstDataRendered={(event) => {
                event.api.forEachNode((node) => {
                  node.setSelected(String(node.data?.code || '') === gridChooseDialog.draftCode);
                });
              }}
              onRowDoubleClicked={(event) => applyGridChooseSelection(event.data?.code)}
              onSelectionChanged={(event) => {
                const selected = event.api.getSelectedRows()?.[0];
                if (selected?.code) {
                  setGridChooseDialog((prev) => ({ ...prev, draftCode: String(selected.code) }));
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGridChooseDialog((prev) => ({ ...prev, open: false }))}>Cancel</Button>
          <Button variant="contained" onClick={() => applyGridChooseSelection()} disabled={!gridChooseDialog.draftCode}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={customerDialogOpen} onClose={() => setCustomerDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          Select Customer
          <Typography variant="body2" color="text.secondary">
            Choose the synced BP customer to retain for the future final AR Invoice Draft.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 1.25, pt: '8px !important' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <TextField
              size="small"
              value={customerSearch}
              onChange={(event) => setCustomerSearch(event.target.value)}
              placeholder="Search customer code, name, or currency..."
              sx={{ minWidth: { xs: '100%', sm: 360 } }}
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="small" label={draftCustomerCode ? `Selected ${draftCustomerCode}` : 'No customer selected'} />
              <Button
                size="small"
                variant="outlined"
                onClick={() => void loadCustomers(customerSearch)}
              >
                Refresh
              </Button>
            </Stack>
          </Stack>
          <Box className="ag-theme-alpine" sx={{ height: { xs: 360, md: 460 }, width: '100%' }}>
            <AgGridReact<CustomerOption>
              ref={customerGridRef}
              rowData={filteredCustomerOptions}
              columnDefs={[
                { field: 'CardCode', headerName: 'Customer Code', checkboxSelection: true, width: 160 },
                { field: 'CardName', headerName: 'Customer Name', flex: 1, minWidth: 280 },
                { field: 'Currency', headerName: 'Currency', width: 110 },
                { field: 'U_Auto_Invoice_Margin', headerName: 'Margin %', width: 110 },
              ]}
              defaultColDef={{ sortable: true, filter: true, resizable: true }}
              rowSelection="single"
              suppressRowClickSelection={false}
              getRowId={(params) => String(params.data.CardCode)}
              onGridReady={syncCustomerGridSelection}
              onFirstDataRendered={syncCustomerGridSelection}
              onRowDoubleClicked={() => applyCustomerSelection()}
              onSelectionChanged={() => {
                const selected = customerGridRef.current?.api?.getSelectedRows()?.[0];
                if (selected?.CardCode) setDraftCustomerCode(String(selected.CardCode));
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomerDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={applyCustomerSelection} disabled={!draftCustomerCode}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={arHeaderProjectDialogOpen} onClose={() => setArHeaderProjectDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          Select Project
          <Typography variant="body2" color="text.secondary">
            This project is used on the unbilled journal entry and retained for the future final AR Invoice Draft.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 1.25, pt: '8px !important' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <TextField
              size="small"
              value={arHeaderProjectSearch}
              onChange={(event) => setArHeaderProjectSearch(event.target.value)}
              placeholder="Search header project..."
              sx={{ minWidth: { xs: '100%', sm: 320 } }}
            />
            <Chip size="small" label={draftArHeaderProjectCode ? `Selected ${draftArHeaderProjectCode}` : 'No header project selected'} />
          </Stack>
          <Box className="ag-theme-alpine" sx={{ height: { xs: 360, md: 460 }, width: '100%' }}>
            <AgGridReact<ProjectOption>
              ref={arHeaderProjectGridRef}
              rowData={filteredArHeaderProjectOptions}
              columnDefs={[
                { field: 'code', headerName: 'Project Code', checkboxSelection: true, width: 170 },
                { field: 'name', headerName: 'Project Name', flex: 1, minWidth: 260 },
              ]}
              defaultColDef={{ sortable: true, filter: true, resizable: true }}
              rowSelection="single"
              suppressRowClickSelection={false}
              getRowId={(params) => String(params.data.code)}
              onGridReady={syncArHeaderProjectGridSelection}
              onFirstDataRendered={syncArHeaderProjectGridSelection}
              onSelectionChanged={() => {
                const selected = arHeaderProjectGridRef.current?.api?.getSelectedRows()?.[0];
                if (selected?.code) setDraftArHeaderProjectCode(String(selected.code));
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArHeaderProjectDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={applyArHeaderProject} disabled={!draftArHeaderProjectCode}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={salesOrderHistoryOpen} onClose={() => setSalesOrderHistoryOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          Sales Order Billing History
          <Typography variant="body2" color="text.secondary">
            Review previous Auto Invoice AR drafts and remaining billable Sales Order amount.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 1.25, pt: '8px !important' }}>
          {salesOrderHistoryLoading ? (
            <Stack spacing={1.5} alignItems="center" sx={{ py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">Loading sales order billing history...</Typography>
            </Stack>
          ) : (
            <>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                <Chip size="small" label={`SO ${salesOrderHistory?.salesOrder?.docNum || salesOrderHistory?.salesOrder?.docEntry || '-'}`} />
                <Chip size="small" label={`Customer ${salesOrderHistory?.salesOrder?.cardCode || '-'}`} />
                <Chip size="small" variant="outlined" label={`Original ${money(salesOrderHistory?.summary?.orderBaseAmount, salesOrderHistory?.salesOrder?.currency)}`} />
                <Chip size="small" variant="outlined" label={`Billed Base ${money(salesOrderHistory?.summary?.billedBaseAmount, salesOrderHistory?.salesOrder?.currency)}`} />
                <Chip size="small" variant="outlined" label={`Billed AR ${money(salesOrderHistory?.summary?.billedInvoiceAmount, salesOrderHistory?.salesOrder?.currency)}`} />
                <Chip
                  size="small"
                  color={(salesOrderHistory?.summary?.remainingBaseAmount || 0) > 0 ? 'warning' : 'success'}
                  label={`Remaining ${money(salesOrderHistory?.summary?.remainingBaseAmount, salesOrderHistory?.salesOrder?.currency)}`}
                />
              </Stack>
              <Box sx={{ height: 360, width: '100%' }}>
                <DataGrid
                  density="compact"
                  rows={(salesOrderHistory?.rows || []).map((row: any) => ({ ...row, id: row.id }))}
                  columns={[
                    { field: 'arinvoicedocnum', headerName: 'AR Draft', width: 100 },
                    { field: 'status', headerName: 'Status', width: 100 },
                    { field: 'baseamount', headerName: 'Billed Base', width: 120, align: 'right', headerAlign: 'right', renderCell: (params) => money(params.value, salesOrderHistory?.salesOrder?.currency) },
                    { field: 'invoiceamount', headerName: 'AR Value', width: 120, align: 'right', headerAlign: 'right', renderCell: (params) => money(params.value, salesOrderHistory?.salesOrder?.currency) },
                    { field: 'projectcode', headerName: 'Project', width: 130 },
                    { field: 'invoicetype', headerName: 'Type', width: 130 },
                    { field: 'marginpercent', headerName: 'Margin %', width: 100 },
                    { field: 'createdbyname', headerName: 'Created By', width: 150 },
                    { field: 'createddate', headerName: 'Created Date', width: 170, renderCell: (params) => params.value ? new Date(params.value as string).toLocaleString() : '-' },
                  ]}
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                  disableRowSelectionOnClick
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSalesOrderHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={manualJEHistoryOpen} onClose={() => setManualJEHistoryOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          Manual JE Billing History
          <Typography variant="body2" color="text.secondary">
            Review previous Auto Invoice AR drafts created against this Manual Journal Entry.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 1.25, pt: '8px !important' }}>
          {manualJEHistoryLoading ? (
            <Stack spacing={1.5} alignItems="center" sx={{ py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">Loading manual JE billing history...</Typography>
            </Stack>
          ) : (
            <>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                <Chip size="small" label={`JE ${manualJEHistory?.manualJournal?.docNum || '-'}`} />
                <Chip size="small" label={`Date ${manualJEHistory?.manualJournal?.docDate || '-'}`} />
                <Chip size="small" variant="outlined" label={`Memo ${manualJEHistory?.manualJournal?.memo || '-'}`} />
                <Chip size="small" variant="outlined" label={`Original ${money(manualJEHistory?.summary?.journalBaseAmount, manualJEHistory?.manualJournal?.currency)}`} />
                <Chip size="small" variant="outlined" label={`Billed Base ${money(manualJEHistory?.summary?.billedBaseAmount, manualJEHistory?.manualJournal?.currency)}`} />
                <Chip size="small" variant="outlined" label={`Billed AR ${money(manualJEHistory?.summary?.billedInvoiceAmount, manualJEHistory?.manualJournal?.currency)}`} />
                <Chip
                  size="small"
                  color={(manualJEHistory?.summary?.remainingBaseAmount || 0) > 0 ? 'warning' : 'success'}
                  label={`Remaining ${money(manualJEHistory?.summary?.remainingBaseAmount, manualJEHistory?.manualJournal?.currency)}`}
                />
              </Stack>
              <Box sx={{ height: 360, width: '100%' }}>
                <DataGrid
                  density="compact"
                  rows={(manualJEHistory?.rows || []).map((row: any) => ({ ...row, id: row.id }))}
                  columns={[
                    { field: 'arinvoicedocnum', headerName: 'AR Draft', width: 100 },
                    { field: 'status', headerName: 'Status', width: 100 },
                    { field: 'baseamount', headerName: 'Billed Base', width: 120, align: 'right', headerAlign: 'right', renderCell: (params) => money(params.value, manualJEHistory?.manualJournal?.currency) },
                    { field: 'invoiceamount', headerName: 'AR Value', width: 120, align: 'right', headerAlign: 'right', renderCell: (params) => money(params.value, manualJEHistory?.manualJournal?.currency) },
                    { field: 'projectcode', headerName: 'Project', width: 130 },
                    { field: 'invoicetype', headerName: 'Type', width: 130 },
                    { field: 'marginpercent', headerName: 'Margin %', width: 100 },
                    { field: 'kpideduction', headerName: 'KPI Deduction', width: 120, align: 'right', headerAlign: 'right', renderCell: (params) => money(params.value, manualJEHistory?.manualJournal?.currency) },
                    { field: 'createdbyname', headerName: 'Created By', width: 150 },
                    { field: 'createddate', headerName: 'Created Date', width: 170, renderCell: (params) => params.value ? new Date(params.value as string).toLocaleString() : '-' },
                  ]}
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                  disableRowSelectionOnClick
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualJEHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={processPreview.open}
        onClose={() => setProcessPreview({ open: false, payload: null, rows: [] })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          {processMode === 'final-unbilled' ? 'Final AR Invoice Draft Preview' : 'Unbilled Revenue Posting Preview'}
        </DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
            <Chip size="small" label={`${processPreview.rows.length} selected`} />
            <Chip size="small" label={`Customer ${customerCode}`} />
            <Chip size="small" label={`Project ${arHeaderProjectCode || '-'}`} />
            <Chip size="small" color="primary" variant="outlined" label={`Posting date ${formatDisplayDate(postingDate)}`} />
            {processMode === 'direct' && <Chip size="small" color="primary" variant="outlined" label={`Margin ${money(marginPercent).replace('.00', '')}%`} />}
            <Chip size="small" color="primary" label={`Total ${money(previewUnbilledTotal, 'SAR')}`} />
            {processMode === 'final-unbilled' && (
              <Chip size="small" variant="outlined" label={draftGrouping === 'separate' ? 'Separate drafts' : 'Combined draft'} />
            )}
          </Stack>

          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.75 }}>GL impact</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1, mb: 1.5 }}>
            {processMode === 'direct' ? (
              <>
                <Paper variant="outlined" sx={{ p: 1.25, borderLeft: '4px solid', borderLeftColor: 'warning.main' }}>
                  <Typography variant="subtitle2" fontWeight={800}>Goods Issue / COGS</Typography>
                  <Stack spacing={0.5} sx={{ mt: 1 }}>
                    {previewAccountingBreakdown.filter((item) => item.cogsAmount > 0).map((item) => (
                      <Box key={item.type}>
                        <Typography variant="body2"><strong>Dr</strong> {item.cogsAccounts.join(', ') || 'SAP-determined COGS'} — {money(item.cogsAmount, 'SAR')}</Typography>
                        <Typography variant="body2"><strong>Cr</strong> {item.creditAccount} — {money(item.cogsAmount, 'SAR')}</Typography>
                      </Box>
                    ))}
                    {!previewAccountingBreakdown.some((item) => item.cogsAmount > 0) && (
                      <Typography variant="body2" color="text.secondary">No COGS posting for the selected transactions.</Typography>
                    )}
                  </Stack>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.25, borderLeft: '4px solid', borderLeftColor: 'info.main' }}>
                  <Typography variant="subtitle2" fontWeight={800}>Unbilled Revenue JE</Typography>
                  <Stack spacing={0.5} sx={{ mt: 1 }}>
                    <Typography variant="body2"><strong>Dr</strong> {unbilledAccountLabel} — {money(previewUnbilledTotal, 'SAR')}</Typography>
                    <Typography variant="body2"><strong>Cr</strong> 411010100 — {money(previewUnbilledTotal, 'SAR')}</Typography>
                    <Typography variant="caption" color="text.secondary">BP {customerCode} · Auxiliary SA00007</Typography>
                  </Stack>
                </Paper>
              </>
            ) : (
              <>
                <Paper variant="outlined" sx={{ p: 1.25, borderLeft: '4px solid', borderLeftColor: 'info.main' }}>
                  <Typography variant="subtitle2" fontWeight={800}>Unbilled Reversal JE</Typography>
                  <Stack spacing={0.5} sx={{ mt: 1 }}>
                    <Typography variant="body2"><strong>Dr</strong> {previewReversalRevenueAccounts.join(', ') || 'Original revenue G/L'} — {money(previewUnbilledTotal, 'SAR')}</Typography>
                    <Typography variant="body2"><strong>Cr</strong> {unbilledAccountLabel} — {money(previewUnbilledTotal, 'SAR')}</Typography>
                    <Typography variant="caption" color="text.secondary">BP {customerCode} · Auxiliary SA00007</Typography>
                  </Stack>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.25, borderLeft: '4px solid', borderLeftColor: 'success.main' }}>
                  <Typography variant="subtitle2" fontWeight={800}>AR Invoice Draft</Typography>
                  <Typography variant="caption" color="text.secondary">No G/L posting until the draft is added as an invoice.</Typography>
                  <Stack spacing={0.5} sx={{ mt: 1 }}>
                    <Typography variant="body2"><strong>Dr</strong> Customer A/R — gross amount</Typography>
                    <Typography variant="body2"><strong>Cr</strong> Revenue and output tax — determined by SAP</Typography>
                    <Typography variant="caption" color="text.secondary">Item and Auxiliary {invoiceItems[invoiceType].itemCode}</Typography>
                  </Stack>
                </Paper>
              </>
            )}
          </Box>

          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.75 }}>Selected documents</Typography>
          <Box sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Box sx={{ minWidth: 620 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '110px 100px minmax(150px, 1fr) minmax(160px, 1fr) 130px', bgcolor: 'action.hover', '& > div': { px: 1, py: 0.75, fontSize: 12, fontWeight: 700 } }}>
                <Box>Source</Box><Box>Document</Box><Box>Item</Box><Box>Project</Box><Box textAlign="right">Amount</Box>
              </Box>
              {processPreview.rows.map((row) => {
                const type = row.transactionType || 'GRPO';
                const amount = rowKpiAllocation.get(row.id)?.final ?? Number(row.salesPrice || 0);
                const docNo = row.documentNo || row.grpoDocNum || row.salesOrderDocEntry || row.journalEntryDocNum || row.grpoDocEntry || '-';
                const item = processMode === 'final-unbilled'
                  ? invoiceItems[invoiceType].itemCode
                  : row.itemCodes?.join(', ') || '-';
                return (
                  <Box key={row.id} sx={{ display: 'grid', gridTemplateColumns: '110px 100px minmax(150px, 1fr) minmax(160px, 1fr) 130px', borderTop: '1px solid', borderColor: 'divider', '& > div': { px: 1, py: 0.8, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis' } }}>
                    <Box>{type}</Box><Box>{docNo}</Box><Box title={item}>{item}</Box><Box>{row.projectCode || '-'}</Box><Box textAlign="right">{money(amount, row.currency || 'SAR')}</Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProcessPreview({ open: false, payload: null, rows: [] })} disabled={processing}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={confirmProcessPreview} disabled={processing}>
            {processMode === 'final-unbilled' ? 'Confirm Final AR Draft' : 'Confirm Unbilled Posting'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={processing} maxWidth="xs" fullWidth>
        <DialogContent>
          <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress />
            <Box textAlign="center">
              <Typography variant="subtitle1" fontWeight={700}>{processMode === 'final-unbilled' ? 'Creating Final AR Draft' : 'Posting Unbilled Revenue'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {processMode === 'final-unbilled'
                  ? 'Creating final AR draft output and reversing selected unbilled revenue journal entries.'
                  : 'Creating required COGS postings and unbilled revenue journal entries. No A/R Invoice Draft is created at this stage.'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                GRPO {selectedTypeCounts.grpo} | Sales Order {selectedTypeCounts.salesOrder} | Manual JE {selectedTypeCounts.manualJE}. Please wait.
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
      <Dialog
        open={processResult.open}
        onClose={() => setProcessResult((prev) => ({ ...prev, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          {processResult.title}
        </DialogTitle>
        <DialogContent>
          <Alert severity={processResult.severity} sx={{ mb: processResult.details || processResult.summary?.length ? 1.5 : 0 }}>
            {processResult.message}
          </Alert>
          {!!processResult.summary?.length && (
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
              {processResult.summary.map((item, index) => (
                <Box
                  key={item.label}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '145px 1fr' },
                    gap: { xs: 0.25, sm: 2 },
                    px: 1.5,
                    py: 1.1,
                    borderTop: index ? '1px solid' : 0,
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                  <Typography variant="body2" fontWeight={700}>{item.value}</Typography>
                </Box>
              ))}
            </Paper>
          )}
          {processResult.details && (
            <Typography variant="body2" color="text.secondary">
              {processResult.details}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {processResult.canViewUnbilledJEs && (
            <Button
              variant="outlined"
              onClick={async () => {
                setProcessResult((prev) => ({ ...prev, open: false }));
                setProcessMode('final-unbilled');
                setDraftGrouping('combined');
                setRows([]);
                setSelection([]);
                await loadGRPOsForMode('final-unbilled');
              }}
            >
              Open Final AR Stage
            </Button>
          )}
          <Button
            variant="contained"
            onClick={() => setProcessResult((prev) => ({ ...prev, open: false }))}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AutoInvoice;
